/* eslint-disable  no-restricted-syntax */
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { useQueryClient, type QueryKey } from '@tanstack/react-query'
import { X, Lock } from '@phosphor-icons/react'
import { Input, Select, SelectTrigger, SelectValue, SelectContent, SelectItem, SelectItemWithIcon, Button, Checkbox } from '@/components/ui'
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  createQueryKey,
  TOTALS_KEY,
  COGS_BASE_KEY,
  PERSONNEL_BASE_KEY,
  OIC_KEY,
  TWC_BASE_KEY,
  VAT_BASE_KEY,
  FINANCIAL_MODEL_SETTINGS_BASE_KEY,
  invalidateGlobalsQueryKey
} from '@/api/query-keys'
import FormattedInput from '@/components/ui/formatted-input'
import { useFormatLocalizedNumber } from '@/lib/hooks/useLocaleFormatter'
import { SidePanelPayload } from '@/lib/sidePanelEmitter'
import { collectForecastPeriods } from '@/utils/periods'
import {
  DirectLabourConfig,
  DirectLabourTableRows,
  ConfigWithoutAllowedTypes as CogsConfigWithoutAllowedTypes,
  GetDirectLabourDataResponse,
  PostDirectLabourBody
} from '@/feature/financial-model/ecommerce/cogs/api/types'
import { postDirectLabourDataBulk } from '@/feature/financial-model/ecommerce/cogs/api'
import { postPersonnelData } from '@/feature/financial-model/ecommerce/personnel/api'
import { GetPersonnelDataResponse, TableRows } from '@/feature/financial-model/ecommerce/personnel/api/types'
import { createOpexQueryKeys, upsertOpexDataEntriesByCategoryId } from '@/feature/financial-model/ecommerce/opex/api'
import { GetOpexDataEntriesResponse } from '@/feature/financial-model/ecommerce/opex/api/types'
import { getPostOicPeriodMutationArgs } from '@/feature/financial-model/ecommerce/oic/api'
import { OICCategoryDataResponse, OicPeriodPostType } from '@/feature/financial-model/ecommerce/oic/api/types'
import { axiosInstance } from '@/api/axios'
import { updateInventoryPeriodData } from '@/feature/financial-model/ecommerce/twc/api/inventory'
import { updatePayablePeriodData } from '@/feature/financial-model/ecommerce/twc/api/payable'
import { updateReceivablePeriodData } from '@/feature/financial-model/ecommerce/twc/api/receivable'
import { TWC_TOTALS_KEY } from '@/feature/financial-model/ecommerce/twc/api/totals'
import { InventoryPeriod, InventoryPayloadKey } from '@/feature/financial-model/ecommerce/twc/types/inventory'
import { PayableBaseFieldsKeys, PayablePeriod } from '@/feature/financial-model/ecommerce/twc/types/payable'
import { ReceivableBaseFieldsKeys, ReceivablePeriod } from '@/feature/financial-model/ecommerce/twc/types/receivable'
import { updateVatPeriodData } from '@/feature/financial-model/ecommerce/vat/api'
import { VatBaseFieldsKeys, VatPeriodDataPost, VatPeriodBaseData } from '@/feature/financial-model/ecommerce/vat/types'
import { createAssetTypeEntriesQueryKey, createBulkUpdateFixedAssetEntriesMutationArgs } from '@/feature/financial-model/ecommerce/fixed-assets/api'
import { AssetTypeEntryPeriod, AssetTypeEntryValues } from '@/feature/financial-model/ecommerce/fixed-assets/api/types'
import {
  PROVISIONS_BAD_DEBT_KEY,
  PROVISIONS_INVENTORY_KEY,
  PROVISIONS_OTHER_KEY,
  PROVISIONS_TOTALS_KEY
} from '@/feature/financial-model/ecommerce/provisions/api'
import {
  BadDebtBaseFieldsKeys,
  BadDebtPeriod,
  BadDebtPeriodConfig,
  BadDebtPeriodDataPost
} from '@/feature/financial-model/ecommerce/provisions/types/bad-debt'
import {
  ProvisionInventoryBaseFieldsKeys,
  ProvisionInventoryPeriodConfig,
  ProvisionInventoryPeriodDataPostV2
} from '@/feature/financial-model/ecommerce/provisions/types/provision-inentory'
import {
  OtherProvisionsBaseFieldsKeys,
  OtherProvisionsPeriodConfig,
  OtherProvisionsPeriodDataPost
} from '@/feature/financial-model/ecommerce/provisions/types/other-provisions'
import { OWC_PERIODS_KEY, OWC_TOTALS_KEY } from '@/feature/financial-model/ecommerce/owc/api'
import {
  CurrentAssetsBaseFieldsKeys,
  CurrentAssetsPeriodConfig,
  CurrentAssetsPeriodDataPost
} from '@/feature/financial-model/ecommerce/owc/types/current-assets'
import {
  CurrentLiabilitiesBaseFieldsKeys,
  CurrentLiabilitiesPeriodConfig,
  CurrentLiabilitiesPeriodDataPost
} from '@/feature/financial-model/ecommerce/owc/types/current-liabilities'
import { EquityBaseFieldsKeys, EquityPeriodConfig, UpdateEquityPeriod } from '@/feature/financial-model/ecommerce/capital-structure/types/equity'
import { GrantBaseFieldsKeys, GrantPeriodConfig, UpdateGrantPeriod } from '@/feature/financial-model/ecommerce/capital-structure/types/grant'
import { EQUITY_DATA_ENTRY_KEY, EQUITY_TOTALS_KEY } from '@/feature/financial-model/ecommerce/capital-structure/api/equity'
import { GRANT_DATA_ENTRY_KEY, GRANT_TOTALS_KEY } from '@/feature/financial-model/ecommerce/capital-structure/api/grant'
import { parsePeriod, filterRowsStartingFrom } from '@/utils/sidepanel'
import { applyEcommerceChannelsValues } from '@/utils/sidepanel/channels'
import { applyPriceValues } from '@/utils/sidepanel/prices'
import {
  applyOrderCompositionValueToRow,
  applyOrderCompositionValues,
  applyOrderDataValueToRow,
  applyOrderDataValues
} from '@/utils/sidepanel/orders'
import applySegmentValueToRow from '@/utils/sidepanel/customer-segments'
import { applyDirectCostValueToRow } from '@/utils/sidepanel/cogs/direct-costs'

type SidePanelProps = {
  open: boolean
  onClose: () => void
  payload?: SidePanelPayload
}

type Method = `all` | `from` | `formula`

type BadDebtPeriodWithIds = BadDebtPeriod & { product_id?: string; category_id?: string }

type PersonnelConfigWithoutAllowedTypes = {
  [Key in keyof TableRows]: {
    type: GetPersonnelDataResponse['config'][Key]['type']
  }
}

type ManualGrowthPreviewRow = {
  year: string
  baseYear: string
  rows: Array<{ key: string; month: { value: string; label: string }; value: number }>
  total: number
}

const ANIMATION_DURATION = 300
const MONTH_NAMES = [`Jan`, `Feb`, `Mar`, `Apr`, `May`, `Jun`, `Jul`, `Aug`, `Sep`, `Oct`, `Nov`, `Dec`]
const FALLBACK_APPLY_ERROR_MESSAGE = `Unable to apply rules. Please try again.`

const FORMULA_OPTIONS = [
  {
    value: `_placeholder`,
    label: `Select Rule`
  },
  {
    value: `smart`,
    label: `Smart Forecasting`,
    description: `Calculate values based on historical values and trends`
  },
  {
    value: `manual_growth`,
    label: `Growth Rate`,
    description: `Calculate values based on specific years performance adjusting per growth rates`
  },
  {
    value: `manual_value_and_growth`,
    label: `Value And Growth`,
    description: `Calculate values, starting from a first year value estimate and future trend changes`
  }
]

const SMART_MONTH_OPTIONS = [3, 6, 12, 24, 36]

const mapConfigToTypes = <T,>(config: any): T => {
  const { view: _view, ...rest } = config
  return Object.keys(rest).reduce((acc: any, key) => {
    acc[key] = { type: rest[key].type }
    return acc
  }, {} as T)
}

const mapDirectLabourConfig = (config: DirectLabourConfig): CogsConfigWithoutAllowedTypes => mapConfigToTypes<CogsConfigWithoutAllowedTypes>(config)

const mapPersonnelConfig = (config: GetPersonnelDataResponse['config']): PersonnelConfigWithoutAllowedTypes =>
  mapConfigToTypes<PersonnelConfigWithoutAllowedTypes>(config)

const getSidePanelApplyErrorMessage = (error: unknown) => {
  if (typeof error === `string`) return error
  if (error instanceof Error) return error.message
  return FALLBACK_APPLY_ERROR_MESSAGE
}

const normalizeNumericInputValue = (value: string) => {
  if (!value) return ''

  const sanitized = value.replace(/,/g, `.`).replace(/[^0-9.-]/g, ``)
  const next = sanitized.match(/-?\d*(?:\.\d*)?/)?.[0]

  return next ?? ''
}

const parseNumericValue = (value: string) => {
  const normalized = normalizeNumericInputValue(value)

  if (!normalized) return null

  const parsed = Number(normalized)

  if (Number.isNaN(parsed)) return null

  return parsed
}

const computeValueFromPercent = (percent: number, totalWithoutCurrent: number) => {
  if (!Number.isFinite(percent) || percent < 0 || percent >= 100) return null

  if (percent === 0) return 0

  const ratio = percent / (100 - percent)

  return ratio * totalWithoutCurrent
}

const MethodAccordionTrigger = ({ title, subtitle }: { title: string; subtitle: string }) => (
  <div className='flex flex-col gap-1 text-left'>
    <div className='font-semibold'>{title}</div>
    <div className='text-sm font-normal text-gray-500'>{subtitle}</div>
  </div>
)

const PreviewTableInputClass = 'text-right w-full'
const PreviewTableRowClass = 'grid grid-cols-8 items-center border border-gray-200'
const PreviewTableInputMonthClass = 'h-full border-r border-gray-200 p-1 col-span-2 font-bold '
const PreviewTableInputValueWrapperClass = 'h-full border-r border-gray-200 p-1 col-span-3 flex justify-end items-center'
const PreviewTableInputPercentageWrapperClass = 'flex h-full justify-end gap-1 p-1 col-span-3 items-center'

const PreviewTableWrapper = ({ children, title, nTables }: { children: React.ReactNode; title: string; nTables: number }) => (
  <div className={`mx-auto min-w-[200px] max-w-[250px] border border-gray-200 text-sm text-secondary ${nTables > 1 ? `` : `mx-auto`}`}>
    <div className='p-1 text-center font-bold'>{title}</div>
    {children}
  </div>
)

const PreviewTableRow = ({ children, month }: { children: React.ReactNode; month: string }) => (
  <div className={`${PreviewTableRowClass} border-b-0`}>
    <div className={PreviewTableInputMonthClass}>{month}</div>
    {children}
  </div>
)

const PreviewTableBottomRow = ({ total }: { total: string }) => {
  const formatNumber = useFormatLocalizedNumber({ minimumFractionDigits: 2, maximumFractionDigits: 2 })
  const parsed = Number(String(total).replace(/,/g, ''))
  const formatted = Number.isFinite(parsed) ? formatNumber(parsed) : String(total)

  return (
    <div className={`${PreviewTableRowClass} font-bold`}>
      <div className={PreviewTableInputMonthClass}>Total</div>
      <div className={PreviewTableInputValueWrapperClass}>{formatted}</div>
      <div className={PreviewTableInputPercentageWrapperClass}>100 %</div>
    </div>
  )
}

const SidePanel = ({ open, onClose, payload }: SidePanelProps) => {
  const [mounted, setMounted] = useState(open)
  const [visible, setVisible] = useState(false)
  const [numberValue, setNumberValue] = useState<string | number>(``)
  const [rawValueInput, setRawValueInput] = useState<string | number>('')
  const [smartMonths, setSmartMonths] = useState<number>(12)
  const [includeSeasonal, setIncludeSeasonal] = useState<boolean>(false)
  const [isApplying, setIsApplying] = useState(false)
  const [applyError, setApplyError] = useState<string | null>(null)
  const [startYear, setStartYear] = useState<string | undefined>(undefined)
  const [startMonth, setStartMonth] = useState<string | undefined>(undefined)
  const [activeMethod, setActiveMethod] = useState<Method | null>(null)
  const [accordionValue, setAccordionValue] = useState<string>('')
  const queryClient = useQueryClient()
  const financialModelId = payload?.financialModelId
  const directCostRefetchTimer = useRef<number | null>(null)
  const directLabourRefetchTimer = useRef<number | null>(null)
  const opexRefetchTimer = useRef<number | null>(null)
  const oicRefetchTimer = useRef<number | null>(null)
  const personnelRefetchTimer = useRef<number | null>(null)
  const forecastPeriods = useMemo(() => collectForecastPeriods(payload), [payload])
  const historicalValues = useMemo(() => payload?.historicalValues ?? [], [payload?.historicalValues])
  const hasHistoricalValues = historicalValues.length > 0

  const historicalMutateKey = useMemo<string | undefined>(() => {
    const mutateKey = payload?.mutateDataKey

    if (!mutateKey) return undefined

    const keyAsString = String(mutateKey)
    const hasNumericValue = historicalValues.some(value => typeof value[keyAsString] === `number`)

    return hasNumericValue ? keyAsString : undefined
  }, [payload?.mutateDataKey, historicalValues])

  const availableYears = useMemo(
    () => Array.from(new Set(forecastPeriods.map(period => period.year))).map(year => year.toString()),
    [forecastPeriods]
  )

  const forecastFirstYear = availableYears[0]

  const monthsByYear = useMemo(
    () =>
      forecastPeriods.reduce<Record<string, { value: string; label: string }[]>>((acc, period) => {
        const yearKey = period.year.toString()

        if (!acc[yearKey]) acc[yearKey] = []

        const monthValue = period.month.toString().padStart(2, `0`)

        if (!acc[yearKey].some(month => month.value === monthValue)) {
          acc[yearKey].push({
            value: monthValue,
            label: MONTH_NAMES[period.month - 1] ?? monthValue
          })
        }

        return acc
      }, {}),
    [forecastPeriods]
  )

  const baseYearMonthlyMap = useMemo(() => {
    const map: Record<string, Record<string, number>> = {}
    const addValue = (yearCandidate: unknown, monthCandidate: unknown, rawValue: unknown, period?: string) => {
      const toNumber = (value: unknown) => {
        if (typeof value === `number`) return value

        if (value == null) return 0

        return parseNumericValue(String(value)) ?? 0
      }

      const normalizedYear = typeof yearCandidate === `number` ? yearCandidate : typeof yearCandidate === `string` ? Number(yearCandidate) : undefined
      const normalizedMonth =
        typeof monthCandidate === `number` ? monthCandidate : typeof monthCandidate === `string` ? Number(monthCandidate) : undefined

      let year = Number.isFinite(normalizedYear ?? NaN) ? normalizedYear : undefined
      let month = Number.isFinite(normalizedMonth ?? NaN) ? normalizedMonth : undefined

      if ((!Number.isFinite(year ?? NaN) || !Number.isFinite(month ?? NaN)) && typeof period === `string`) {
        const { year: parsedYear, month: parsedMonth } = parsePeriod(period)

        if (parsedYear !== undefined) year = parsedYear

        if (parsedMonth !== undefined) month = parsedMonth
      }

      if (!Number.isFinite(year ?? NaN) || !Number.isFinite(month ?? NaN)) return

      const yearKey = String(year)
      const monthKey = String(Number(month)).padStart(2, `0`)
      const value = toNumber(rawValue)

      if (!map[yearKey]) map[yearKey] = {}

      map[yearKey][monthKey] = value
    }

    const mutateDataKey = payload?.mutateDataKey
    const periodType = payload?.queryPeriod ?? `forecast`

    const process = (items: any[] | undefined, shouldProcess: boolean, filterFn: (item: any) => boolean, valueFn: (item: any) => any) => {
      if (items?.length && shouldProcess) {
        items.filter(filterFn).forEach(item => {
          addValue(item.fiscal_year, item.fiscal_month, valueFn(item), item.period)
        })
      }
    }

    const isForecast = (item: any) => item.period_type === periodType
    const isTypeForecast = (item: any) => item.type === periodType

    if (mutateDataKey) {
      const getValue = (item: any) => item[mutateDataKey]

      process(payload?.product?.prices, true, isForecast, getValue)
      process(payload?.historicalValues, true, () => true, getValue)
      process(payload?.orderProduct?.orders, mutateDataKey === 'value', isForecast, item => item.value)
      process(payload?.orderDataPeriods, true, isForecast, item => item.order_data?.[mutateDataKey])

      const standardCollections = [
        payload?.directLabourPeriods,
        payload?.personnelPeriods,
        payload?.directCostPeriods,
        payload?.receivablePeriods,
        payload?.inventoryPeriods,
        payload?.payablePeriods,
        payload?.grantPeriods,
        payload?.equityPeriods,
        payload?.provisionPeriods,
        payload?.provisionInventoryPeriods,
        payload?.otherProvisionsPeriods,
        payload?.owcPeriods,
        payload?.fixedAssetPeriods,
        payload?.vatPeriods
      ]

      standardCollections.forEach(collection => {
        process(collection, true, isForecast, getValue)
      })

      process(payload?.organicDirectResidualOrdersPeriods, true, item => item.type === periodType || item.period_type === periodType, getValue)
    }

    process(
      payload?.customerSegmentPeriods,
      !!payload?.customerSegmentRowKey,
      item => item.period_type === 'forecast',
      item => (payload?.customerSegmentRowKey ? item[payload.customerSegmentRowKey] : 0)
    )

    process(payload?.opexPeriods, true, isForecast, item => item.input_value)
    process(payload?.oicPeriods, mutateDataKey === 'input_value', isTypeForecast, item => item.input_value)

    const allocationCollections = [
      payload?.marketingOrderAllocationPeriods,
      payload?.salesOrdersAllocationPeriods,
      payload?.organicOrdersAllocationPeriods
    ]

    allocationCollections.forEach(collection => {
      process(collection, (mutateDataKey as string) === 'order_percentage', isTypeForecast, item => item.order_percentage)
    })

    return map
  }, [payload])

  const getBaseMonthValue = useCallback(
    (yearKey: string, monthValue: string) => {
      const yearMap = baseYearMonthlyMap[yearKey]

      if (!yearMap) return 0

      const normalizedMonth = monthValue.padStart(2, `0`)

      return yearMap[normalizedMonth] ?? 0
    },
    [baseYearMonthlyMap]
  )

  const availableMonthsForSelectedYear = useMemo(() => (startYear ? (monthsByYear[startYear] ?? []) : []), [monthsByYear, startYear])

  const latestHistoricalYear = useMemo(() => {
    if (!historicalValues.length) return undefined
    return String(Math.max(...historicalValues.map(price => price.fiscal_year)))
  }, [historicalValues])

  const manualGrowthBaseYearOptions = useMemo(() => {
    const options = [...availableYears]
    if (latestHistoricalYear && !options.includes(latestHistoricalYear)) {
      options.unshift(latestHistoricalYear)
    }
    return options
  }, [availableYears, latestHistoricalYear])

  const historicalMonthEntries = useMemo(() => {
    if (!historicalMutateKey) return []

    const entries = historicalValues
      .map(value => {
        const year = Number(value.fiscal_year)
        const month = Number(value.fiscal_month)
        const raw = value[historicalMutateKey]
        const parsed = Number(raw)

        if (!Number.isFinite(year) || !Number.isFinite(month)) return null

        return {
          year,
          month,
          value: Number.isFinite(parsed) ? parsed : 0
        }
      })
      .filter((entry): entry is { year: number; month: number; value: number } => Boolean(entry))
      .sort((a, b) => a.year - b.year || a.month - b.month)

    return entries
  }, [historicalValues, historicalMutateKey])

  const smartMonthOptions = useMemo(() => {
    if (!historicalMonthEntries.length) return []

    return SMART_MONTH_OPTIONS.filter(option => option <= historicalMonthEntries.length)
  }, [historicalMonthEntries.length])

  const canUseSmartHistoricalData = Boolean(historicalMutateKey && smartMonthOptions.length > 0)

  const availableFormulaOptions = useMemo(
    () =>
      FORMULA_OPTIONS.filter(option => option.value !== `_placeholder`).map(option => {
        if (option.value === `smart`) return { ...option, disabled: !canUseSmartHistoricalData }
        if (option.value === `manual_growth`) return { ...option, disabled: !canUseSmartHistoricalData }

        return { ...option, disabled: false }
      }),
    [canUseSmartHistoricalData]
  )

  const getManualGrowthBaseYearOptionsForRow = useCallback(
    (yearValue: string | number) => {
      const targetYear = Number(yearValue)
      if (Number.isNaN(targetYear)) return []

      return manualGrowthBaseYearOptions
        .filter(option => {
          const optionYear = Number(option)

          return !Number.isNaN(optionYear) && optionYear < targetYear
        })
        .sort((a, b) => Number(b) - Number(a))
    },
    [manualGrowthBaseYearOptions]
  )

  const historicalDistribution = useMemo(() => {
    const totals: Record<string, number> = {}
    const monthTotals: Record<string, Record<string, number>> = {}

    if (!historicalMutateKey) return { totals, monthTotals }

    historicalValues.forEach(price => {
      const yearKey = price.fiscal_year.toString()
      const monthKey = price.fiscal_month.toString().padStart(2, `0`)
      const value = price[historicalMutateKey] ?? 0

      totals[yearKey] = (totals[yearKey] ?? 0) + Number(value)
      const monthMap = monthTotals[yearKey] ?? {}
      monthMap[monthKey] = (monthMap[monthKey] ?? 0) + Number(value)
      monthTotals[yearKey] = monthMap
    })

    return { totals, monthTotals }
  }, [historicalMutateKey, historicalValues])

  const canUseHistoricalSeasonality = hasHistoricalValues && Boolean(historicalMutateKey)

  const getHistoricalMonthShare = useCallback(
    (yearKey: string, monthValue: string) => {
      const yearTotal = historicalDistribution.totals[yearKey] ?? 0
      if (!yearTotal) return undefined

      const monthMap = historicalDistribution.monthTotals[yearKey]
      if (!monthMap) return undefined

      const normalizedMonth = monthValue.padStart(2, `0`)
      const monthValueSum = monthMap[normalizedMonth] ?? 0
      return monthValueSum / yearTotal
    },
    [historicalDistribution]
  )

  useEffect(() => {
    if (!availableYears.length) {
      setStartYear(undefined)

      return
    }
    if (!startYear || !availableYears.includes(startYear)) {
      setStartYear(availableYears[0])
    }
  }, [availableYears, startYear])

  useEffect(() => {
    if (!startYear) {
      setStartMonth(undefined)

      return
    }
    if (!availableMonthsForSelectedYear.length) {
      setStartMonth(undefined)

      return
    }

    if (!startMonth || !availableMonthsForSelectedYear.some(month => month.value === startMonth)) {
      setStartMonth(availableMonthsForSelectedYear[0].value)
    }
  }, [availableMonthsForSelectedYear, startYear, startMonth])

  const resetUnusedMethodValues = useCallback((method: Method) => {
    if (method !== `all`) {
      setNumberValue(``)
    }

    if (method !== `from`) {
      setRawValueInput(``)
      setStartYear(undefined)
      setStartMonth(undefined)
    }

    if (method !== `formula`) {
      setSmartMonths(12)
      setIncludeSeasonal(false)
    }
  }, [])

  const resetAllInputs = useCallback(() => {
    setNumberValue(``)
    setRawValueInput(``)
    setStartYear(undefined)
    setStartMonth(undefined)
    setSmartMonths(12)
    setIncludeSeasonal(false)
    setActiveMethod(null)
    setManualValuesByYear({})
    setMonthlyValueOverrides({})
    setMonthlyPercentDrafts({})
    setYearlyValueOverrides({})
    setYearlyPercentDrafts({})
    setSelectedFormulaOption(FORMULA_OPTIONS[0].value)
    setManualBaseOption('monthly_base')
    setManualGrowthSettings({})
    setSmartForecastMode('linear')
  }, [])

  const [selectedFormulaOption, setSelectedFormulaOption] = useState<string>(() => FORMULA_OPTIONS[0].value)

  const selectedFormulaLabel =
    FORMULA_OPTIONS.find(option => option.value === selectedFormulaOption)?.label ??
    availableFormulaOptions.find(option => option.value === selectedFormulaOption)?.label ??
    availableFormulaOptions[0]?.label ??
    FORMULA_OPTIONS[0].label

  useEffect(() => {
    const selectedOption = availableFormulaOptions.find(option => option.value === selectedFormulaOption)

    if (selectedFormulaOption !== FORMULA_OPTIONS[0].value && (!selectedOption || selectedOption.disabled)) {
      const firstEnabledOption = availableFormulaOptions.find(option => !option.disabled)
      setSelectedFormulaOption(firstEnabledOption?.value ?? FORMULA_OPTIONS[0].value)
    }
  }, [availableFormulaOptions, selectedFormulaOption])

  useEffect(() => {
    if (!smartMonthOptions.length) return
    if (smartMonthOptions.includes(smartMonths)) return

    setSmartMonths(smartMonthOptions[smartMonthOptions.length - 1])
  }, [smartMonthOptions, smartMonths])
  const [manualBaseOption, setManualBaseOption] = useState<'monthly_base' | 'yearly_base'>('monthly_base')
  const [manualValuesByYear, setManualValuesByYear] = useState<Record<string, { value: string; percent: string }>>({})

  const [manualSeasonalityOption, setManualSeasonalityOption] = useState<'include_seasonality' | 'dont_include_seasonality'>(
    'dont_include_seasonality'
  )

  const [monthlyValueOverrides, setMonthlyValueOverrides] = useState<Record<string, string>>({})
  const [monthlyPercentDrafts, setMonthlyPercentDrafts] = useState<Record<string, string>>({})
  const [yearlyValueOverrides, setYearlyValueOverrides] = useState<Record<string, string>>({})
  const [yearlyPercentDrafts, setYearlyPercentDrafts] = useState<Record<string, string>>({})
  const [manualGrowthSettings, setManualGrowthSettings] = useState<Record<string, { baseYear: string; growth: string }>>({})
  const [smartForecastMode, setSmartForecastMode] = useState<'linear' | 'average'>('linear')

  useEffect(() => {
    setMonthlyValueOverrides({})
    setMonthlyPercentDrafts({})
    setYearlyValueOverrides({})
    setYearlyPercentDrafts({})
  }, [manualBaseOption, selectedFormulaOption])

  useEffect(() => {
    if (selectedFormulaOption !== `manual_growth`) return
    if (!availableYears.length) {
      setManualGrowthSettings({})
      return
    }

    setManualGrowthSettings(prev => {
      const next: Record<string, { baseYear: string; growth: string }> = {}
      availableYears.forEach((year, idx) => {
        const prevEntry = prev[year]
        const fallbackBase = idx === 0 ? (latestHistoricalYear ?? availableYears[0] ?? year) : (availableYears[idx - 1] ?? year)
        next[year] = {
          baseYear: prevEntry?.baseYear ?? fallbackBase,
          growth: prevEntry?.growth ?? ''
        }
      })
      return next
    })
  }, [availableYears, latestHistoricalYear, selectedFormulaOption])

  const manualGrowthPreviewData = useMemo((): ManualGrowthPreviewRow[] => {
    if (selectedFormulaOption !== `manual_growth`) return []

    const computedMonthlyMap: Record<string, Record<string, number>> = {}

    return availableYears
      .map((year, idx) => {
        const entry = manualGrowthSettings[year]
        const growthInput = entry?.growth ?? ''
        if (growthInput === '') return null

        const parsedGrowth = parseNumericValue(growthInput)
        if (parsedGrowth === null) return null

        const baseYear = entry?.baseYear ?? (idx === 0 ? (latestHistoricalYear ?? availableYears[0] ?? year) : (availableYears[idx - 1] ?? year))
        if (!baseYear) return null

        const months = monthsByYear?.[year] ?? []
        if (!months.length) return null

        const rate = parsedGrowth / 100
        const selectedBaseYearValues = computedMonthlyMap[baseYear] ?? baseYearMonthlyMap[baseYear] ?? {}

        const rows = months.map(month => {
          const key = `manual_growth-${year}-${month.value}`
          const normalizedMonth = month.value.padStart(2, `0`)
          const baseValue = selectedBaseYearValues[normalizedMonth] ?? 0
          const computedValue = Number((baseValue * (1 + rate)).toFixed(2))
          const overrideValue = parseNumericValue(monthlyValueOverrides[key])
          const value = overrideValue ?? computedValue

          return { key, month, value }
        })

        computedMonthlyMap[year] = rows.reduce<Record<string, number>>((acc, row) => {
          acc[row.month.value.padStart(2, `0`)] = row.value
          return acc
        }, {})

        const total = rows.reduce((acc, row) => acc + row.value, 0)

        return { year, baseYear, rows, total }
      })
      .filter(Boolean) as ManualGrowthPreviewRow[]
  }, [availableYears, baseYearMonthlyMap, latestHistoricalYear, manualGrowthSettings, monthsByYear, monthlyValueOverrides, selectedFormulaOption])

  const handleMonthlyValueInputChange = useCallback(
    (key: string, rawValue: string) => {
      const normalized = normalizeNumericInputValue(rawValue)

      setMonthlyPercentDrafts(prev => {
        const { [key]: _omit, ...rest } = prev
        return rest
      })

      setMonthlyValueOverrides(prev => {
        if (normalized === '') {
          const { [key]: _omit, ...rest } = prev
          return rest
        }

        return {
          ...prev,
          [key]: normalized
        }
      })
    },
    [setMonthlyPercentDrafts, setMonthlyValueOverrides]
  )

  const handleMonthlyPercentDraftChange = useCallback(
    (key: string, rawValue: string) => {
      const normalized = normalizeNumericInputValue(rawValue.replace(/%/g, ``))

      setMonthlyPercentDrafts(prev => ({
        ...prev,
        [key]: normalized
      }))
    },
    [setMonthlyPercentDrafts]
  )

  const handleMonthlyPercentCommit = useCallback(
    (key: string, rawValue: string, currentValue: number, total: number) => {
      setMonthlyPercentDrafts(prev => {
        const { [key]: _omit, ...rest } = prev
        return rest
      })

      const parsedPercent = parseNumericValue(rawValue)

      if (parsedPercent === null) {
        return
      }

      const totalWithoutCurrent = total - currentValue
      const nextValue = computeValueFromPercent(parsedPercent, totalWithoutCurrent)

      if (nextValue === null) {
        return
      }

      setMonthlyValueOverrides(prev => ({
        ...prev,
        [key]: normalizeNumericInputValue(nextValue.toFixed(2))
      }))
    },
    [setMonthlyPercentDrafts, setMonthlyValueOverrides]
  )

  const handleYearlyValueInputChange = useCallback(
    (key: string, rawValue: string) => {
      const normalized = normalizeNumericInputValue(rawValue)

      setYearlyPercentDrafts(prev => {
        const { [key]: _omit, ...rest } = prev
        return rest
      })

      setYearlyValueOverrides(prev => {
        if (normalized === '') {
          const { [key]: _omit, ...rest } = prev
          return rest
        }

        return {
          ...prev,
          [key]: normalized
        }
      })
    },
    [setYearlyPercentDrafts, setYearlyValueOverrides]
  )

  const handleYearlyPercentDraftChange = useCallback(
    (key: string, rawValue: string) => {
      const normalized = normalizeNumericInputValue(rawValue.replace(/%/g, ``))
      setYearlyPercentDrafts(prev => ({
        ...prev,
        [key]: normalized
      }))
    },
    [setYearlyPercentDrafts]
  )

  const handleYearlyPercentCommit = useCallback(
    (key: string, rawValue: string, currentValue: number, total: number) => {
      setYearlyPercentDrafts(prev => {
        const { [key]: _omit, ...rest } = prev
        return rest
      })

      const parsedPercent = parseNumericValue(rawValue)

      if (parsedPercent === null) {
        return
      }

      const totalWithoutCurrent = total - currentValue
      const nextValue = computeValueFromPercent(parsedPercent, totalWithoutCurrent)

      if (nextValue === null) {
        return
      }

      setYearlyValueOverrides(prev => ({
        ...prev,
        [key]: normalizeNumericInputValue(nextValue.toFixed(2))
      }))
    },
    [setYearlyPercentDrafts, setYearlyValueOverrides]
  )

  const handleMethodActivation = useCallback(
    (method: Method) => {
      if (activeMethod !== method) {
        resetUnusedMethodValues(method)
        setActiveMethod(method)
        setAccordionValue(method)
      }
    },
    [activeMethod, resetUnusedMethodValues]
  )

  const handleAccordionChange = useCallback(
    (value: string) => {
      setAccordionValue(value)
      if (value && (value === 'all' || value === 'from' || value === 'formula')) {
        handleMethodActivation(value as Method)
      }
    },
    [handleMethodActivation]
  )

  const applyDirectLabourValueToRow = useCallback(
    async (
      value: number,
      startFrom?: { year: number; month: number },
      _exactPeriod?: { year: number; month: number },
      periodValuesByKeyArg?: Record<string, number>
    ) => {
      if (!financialModelId || !payload?.directLabourPeriods || !payload.mutateDataKey) return

      const mutateDataKey = payload.mutateDataKey as keyof DirectLabourTableRows
      const periodValuesByKey = periodValuesByKeyArg
      const { directLabourPeriods } = payload
      const forecastRows = directLabourPeriods.filter(period => period.period_type === `forecast`)

      if (!forecastRows.length) return

      const rowsToUpdate = periodValuesByKey
        ? forecastRows.filter(period => {
            const periodKey = `${period.fiscal_year}-${String(period.fiscal_month).padStart(2, `0`)}`
            return periodValuesByKey[periodKey] !== undefined
          })
        : filterRowsStartingFrom(forecastRows, startFrom, _exactPeriod)

      if (!rowsToUpdate.length) return

      const directLabourQueryKey = createQueryKey({
        baseKey: COGS_BASE_KEY,
        keys: [`direct-labour`, financialModelId, payload?.queryPeriod ?? `forecast`, payload?.view ?? `monthly`]
      })

      const updatedPeriodIds = new Set(rowsToUpdate.map(p => p.id))

      queryClient.setQueryData<GetDirectLabourDataResponse>(directLabourQueryKey, current => {
        if (!current) return current
        return {
          ...current,
          periods: current.periods.map(period => {
            if (!updatedPeriodIds.has(period.id)) return period

            const periodKey = `${period.fiscal_year}-${String(period.fiscal_month).padStart(2, `0`)}`
            const mappedValue = periodValuesByKey?.[periodKey]

            return { ...period, [mutateDataKey]: mappedValue ?? value }
          })
        }
      })

      const payloads: PostDirectLabourBody[] = rowsToUpdate.map(period => {
        const periodKey = `${period.fiscal_year}-${String(period.fiscal_month).padStart(2, `0`)}`
        const mappedValue = periodValuesByKey?.[periodKey]
        const valueToApply = mappedValue ?? value

        return {
          fiscal_year: period.fiscal_year,
          fiscal_month: period.fiscal_month,
          period_type: period.period_type,
          view: payload.view ?? `monthly`,
          config: mapDirectLabourConfig(period.config),
          total_fte: mutateDataKey === `total_fte` ? valueToApply : period.total_fte,
          avg_annual_gross_salary: mutateDataKey === `avg_annual_gross_salary` ? valueToApply : period.avg_annual_gross_salary,
          social_contributions: mutateDataKey === `social_contributions` ? valueToApply : period.social_contributions,
          other_charges: mutateDataKey === `other_charges` ? valueToApply : period.other_charges,
          bonus: mutateDataKey === `bonus` ? valueToApply : period.bonus,
          percentage_of_direct_labour: mutateDataKey === `percentage_of_direct_labour` ? valueToApply : period.percentage_of_direct_labour
        }
      })

      try {
        await postDirectLabourDataBulk(financialModelId, payloads)
      } catch (err) {
        console.error(`Error applying direct labour value to rows`, err)
        queryClient.invalidateQueries({ queryKey: directLabourQueryKey })
      }

      queryClient.resetQueries({
        queryKey: createQueryKey({
          baseKey: TOTALS_KEY,
          keys: [financialModelId, `direct-labour`]
        })
      })

      if (directLabourRefetchTimer.current) {
        window.clearTimeout(directLabourRefetchTimer.current)
      }

      directLabourRefetchTimer.current = window.setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: directLabourQueryKey })
        directLabourRefetchTimer.current = null
      }, 800)
    },
    [financialModelId, payload, queryClient]
  )

  const applyPersonnelValueToRow = useCallback(
    async (value: number, startFrom?: { year: number; month: number }) => {
      if (!financialModelId || !payload?.personnelPeriods || !payload?.departmentId) return

      const { departmentId } = payload
      const mutateDataKey = payload.mutateDataKey as keyof TableRows | undefined

      if (!mutateDataKey) return

      const forecastRows = payload.personnelPeriods.filter(period => period.period_type === `forecast`)

      if (!forecastRows.length) return

      const rowsToUpdate = filterRowsStartingFrom(forecastRows, startFrom)

      if (!rowsToUpdate.length) return

      await Promise.all(
        rowsToUpdate.map(period => {
          const { year, month } = parsePeriod(period.period)

          const basePayload = {
            department_id: departmentId,
            fiscal_month: month ?? 0,
            fiscal_year: year ?? 0,
            period_type: period.period_type,
            view: payload.view ?? `monthly`,
            config: mapPersonnelConfig(period.config)
          }

          const mutationPayload = {
            ...basePayload,
            total_fte: mutateDataKey === `total_fte` ? value : period.total_fte,
            avg_annual_gross_salary: mutateDataKey === `avg_annual_gross_salary` ? value : period.avg_annual_gross_salary,
            social_contributions: mutateDataKey === `social_contributions` ? value : period.social_contributions,
            other_charges: mutateDataKey === `other_charges` ? value : period.other_charges,
            bonus: mutateDataKey === `bonus` ? value : period.bonus
          }

          return postPersonnelData(financialModelId, mutationPayload)
        })
      )

      const personnelQueryKey = createQueryKey({
        baseKey: PERSONNEL_BASE_KEY,
        keys: [financialModelId, `personnel-data`, departmentId, payload.queryPeriod ?? `forecast`, payload.view ?? `monthly`]
      })

      const updatedPeriodIds = new Set(rowsToUpdate.map(period => period.id))

      queryClient.setQueryData<GetPersonnelDataResponse>(personnelQueryKey, current => {
        if (!current) return current

        return {
          ...current,
          periods: current.periods.map(period => {
            if (!updatedPeriodIds.has(period.id)) return period

            return {
              ...period,
              [mutateDataKey]: value
            }
          })
        }
      })

      if (personnelRefetchTimer.current) {
        window.clearTimeout(personnelRefetchTimer.current)
      }

      personnelRefetchTimer.current = window.setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: personnelQueryKey
        })
        personnelRefetchTimer.current = null
      }, 800)

      queryClient.resetQueries({
        queryKey: createQueryKey({
          baseKey: TOTALS_KEY,
          keys: [financialModelId, `personnel`]
        })
      })
    },
    [financialModelId, payload, queryClient]
  )

  const applyOpexValueToRow = useCallback(
    async (value: number, startFrom?: { year: number; month: number }, exactPeriod?: { year: number; month: number }) => {
      const opexCategoryId = payload?.opexCategoryId

      if (!financialModelId || !payload?.opexPeriods || !opexCategoryId) return

      const forecastRows = payload.opexPeriods.filter(period => period.period_type === `forecast`)

      if (!forecastRows.length) return

      const rowsToUpdate = filterRowsStartingFrom(forecastRows, startFrom, exactPeriod)

      if (!rowsToUpdate.length) return

      await Promise.all(
        rowsToUpdate.map(period => {
          const { year, month } = parsePeriod(period.period)

          return upsertOpexDataEntriesByCategoryId({
            financialModelId,
            data: {
              opex_category_id: opexCategoryId,
              month: month ?? 0,
              year: year ?? 0,
              data_type: period.type,
              input_value: value,
              config: period.config
            }
          })
        })
      )

      const opexQueryKey = createOpexQueryKeys.getOpexDataEntriesByCategoryId({
        financialModelId,
        opexCategoryId,
        queryPeriod: payload.queryPeriod ?? `forecast`,
        view: payload.view ?? `monthly`
      })

      const updatedPeriodIds = new Set(rowsToUpdate.map(period => period.id))

      queryClient.setQueryData<GetOpexDataEntriesResponse>(opexQueryKey, current => {
        if (!current) return current

        return {
          ...current,
          periods: current.periods.map(period => {
            if (!updatedPeriodIds.has(period.id)) return period

            return {
              ...period,
              input_value: value.toString()
            }
          })
        }
      })

      if (opexRefetchTimer.current) {
        window.clearTimeout(opexRefetchTimer.current)
      }

      opexRefetchTimer.current = window.setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: opexQueryKey
        })
        opexRefetchTimer.current = null
      }, 800)

      queryClient.resetQueries({
        queryKey: createOpexQueryKeys.getTotals({
          financialModelId,
          view: payload.view ?? `monthly`
        })
      })
    },
    [financialModelId, payload, queryClient]
  )

  const applyOicValueToRow = useCallback(
    async (value: number, startFrom?: { year: number; month: number }, exactPeriod?: { year: number; month: number }) => {
      const oicCategoryId = payload?.oicCategoryId

      if (!financialModelId || !payload?.oicPeriods || !oicCategoryId) return

      const forecastRows = payload.oicPeriods.filter(period => period.type === `forecast`)

      if (!forecastRows.length) return

      const rowsToUpdate = filterRowsStartingFrom(forecastRows, startFrom, exactPeriod)

      if (!rowsToUpdate.length) return

      const { mutationFn } = getPostOicPeriodMutationArgs(financialModelId, queryClient)

      await Promise.all(
        rowsToUpdate.map(period => {
          const { year, month } = parsePeriod(period.period)

          const body = {
            oic_category_id: oicCategoryId,
            month: month ?? 0,
            year: year ?? 0,
            data_type: period.type,
            input_value: value,
            config: period.config
          } satisfies OicPeriodPostType

          return mutationFn(body)
        })
      )

      const oicQueryKey = createQueryKey({
        baseKey: OIC_KEY,
        keys: [financialModelId, oicCategoryId, payload.queryPeriod ?? `forecast`, payload.view ?? `monthly`]
      })

      const updatedPeriodIds = new Set(rowsToUpdate.map(period => period.period))

      queryClient.setQueryData<OICCategoryDataResponse>(oicQueryKey, current => {
        if (!current) return current

        return {
          ...current,
          periods: current.periods.map(period =>
            updatedPeriodIds.has(period.period)
              ? {
                  ...period,
                  input_value: value.toString()
                }
              : period
          )
        }
      })

      if (oicRefetchTimer.current) {
        window.clearTimeout(oicRefetchTimer.current)
      }

      oicRefetchTimer.current = window.setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: oicQueryKey
        })
        oicRefetchTimer.current = null
      }, 800)

      queryClient.resetQueries({
        queryKey: createQueryKey({
          baseKey: TOTALS_KEY,
          keys: [financialModelId, `oic`]
        })
      })
    },
    [financialModelId, payload, queryClient]
  )

  const applyReceivableValueToRow = useCallback(
    async (value: number, startFrom?: { year: number; month: number }, exactPeriod?: { year: number; month: number }) => {
      if (!financialModelId) return

      const mutateDataKey = payload?.mutateDataKey as ReceivableBaseFieldsKeys | undefined

      if (!mutateDataKey || !payload?.receivablePeriods?.length) return

      const forecastRows = payload.receivablePeriods.filter((period: ReceivablePeriod) => period.period_type === `forecast`)

      if (!forecastRows.length) return

      const rowsToUpdate = filterRowsStartingFrom(forecastRows, startFrom, exactPeriod)

      if (!rowsToUpdate.length) return

      const mutationPayloads = rowsToUpdate.map(period => {
        const { year, month } = parsePeriod(period.period)

        return {
          financialModelId,
          data: {
            ...period,
            year: year ?? 0,
            month: month ?? 0,
            period_type: period.period_type,
            product_id: payload.receivableProductId ?? null,
            customer_segment_id: payload.receivableCustomerSegmentId ?? null,
            config: {
              ...period.config,
              [mutateDataKey]: {
                type: period.config[mutateDataKey]?.type ?? `absolute`,
                allowed_types: period.config[mutateDataKey]?.allowed_types ?? []
              }
            },
            [mutateDataKey]: value
          }
        }
      })

      await Promise.allSettled(mutationPayloads.map(p => updateReceivablePeriodData(p)))

      const queryPeriod = payload.queryPeriod ?? `forecast`
      const view = payload.view ?? `monthly`

      const receivablePeriodQueryKey = createQueryKey({
        baseKey: TWC_BASE_KEY,
        keys: [`receivable`, `period-data`, financialModelId as string, queryPeriod as string, view as string]
      })

      await queryClient.invalidateQueries({ queryKey: receivablePeriodQueryKey as QueryKey })

      const receivableTotalsQueryKey = createQueryKey({
        baseKey: TWC_TOTALS_KEY,
        keys: [`receivable`, financialModelId as string, queryPeriod as string, view as string]
      })

      await queryClient.invalidateQueries({ queryKey: receivableTotalsQueryKey as QueryKey })

      try {
        await queryClient.refetchQueries({ queryKey: receivablePeriodQueryKey as QueryKey, exact: true })
        await queryClient.refetchQueries({ queryKey: receivableTotalsQueryKey as QueryKey, exact: true })
      } catch (error) {
        console.error('Error refetching receivable data after update:', error)
      }
    },
    [financialModelId, payload, queryClient]
  )

  const applyOwcValueToRow = useCallback(
    async (value: number, startFrom?: { year: number; month: number }, exactPeriod?: { year: number; month: number }) => {
      if (!financialModelId) return

      const mutateDataKey = payload?.mutateDataKey as CurrentAssetsBaseFieldsKeys | CurrentLiabilitiesBaseFieldsKeys | undefined
      const configType = payload?.owcConfigType
      const periods = payload?.owcPeriods
      const categoryId = payload?.owcCategoryId ?? null

      if (!mutateDataKey || !configType || !periods?.length) return

      const forecastRows = periods.filter(period => period.period_type === `forecast`)

      if (!forecastRows.length) return

      const rowsToUpdate = filterRowsStartingFrom(forecastRows, startFrom, exactPeriod)

      if (!rowsToUpdate.length) return

      await Promise.all(
        rowsToUpdate.map(period => {
          const { year, month } = parsePeriod(period.period)

          if (configType === `current-assets`) {
            const { field_disabled: _ignored, ...configWithoutDisabled } = period.config as CurrentAssetsPeriodConfig
            const mutationPayload: CurrentAssetsPeriodDataPost = {
              config: configWithoutDisabled as Exclude<CurrentAssetsPeriodConfig, 'field_disabled'>,
              fiscal_year: year ?? 0,
              fiscal_month: month ?? 0,
              period_type: period.period_type,
              category_id: categoryId ?? period.category_id,
              [mutateDataKey]: value
            }
            return axiosInstance.post(`/financial-models/${financialModelId}/owc/assets-liabilities/assets/entries`, mutationPayload)
          }

          const { field_disabled: _ignored, ...configWithoutDisabled } = period.config as CurrentLiabilitiesPeriodConfig
          const mutationPayload: CurrentLiabilitiesPeriodDataPost = {
            config: configWithoutDisabled as Exclude<CurrentLiabilitiesPeriodConfig, 'field_disabled'>,
            fiscal_year: year ?? 0,
            fiscal_month: month ?? 0,
            period_type: period.period_type,
            category_id: categoryId ?? period.category_id,
            [mutateDataKey]: value
          }
          return axiosInstance.post(`/financial-models/${financialModelId}/owc/assets-liabilities/liabilities/entries`, mutationPayload)
        })
      )

      queryClient.invalidateQueries({
        queryKey: OWC_PERIODS_KEY.concat([configType])
      })
      queryClient.resetQueries({
        queryKey: OWC_TOTALS_KEY
      })
    },
    [financialModelId, payload, queryClient]
  )

  const applyEquityValueToRow = useCallback(
    async (value: number, startFrom?: { year: number; month: number }, exactPeriod?: { year: number; month: number }) => {
      if (!financialModelId) return

      const mutateDataKey = payload?.mutateDataKey as EquityBaseFieldsKeys | undefined
      const periods = payload?.equityPeriods

      if (!mutateDataKey || !periods?.length) return

      const forecastRows = periods.filter(period => period.period_type === `forecast`)

      if (!forecastRows.length) return

      const rowsToUpdate = filterRowsStartingFrom(forecastRows, startFrom, exactPeriod)

      if (!rowsToUpdate.length) return

      await Promise.all(
        rowsToUpdate.map(period => {
          const { year, month } = parsePeriod(period.period)
          const { field_disabled: _ignored, ...configWithoutDisabled } = period.config
          const mutationPayload: UpdateEquityPeriod = {
            config: configWithoutDisabled as Exclude<EquityPeriodConfig, 'field_disabled'>,
            period_type: period.period_type,
            year: year ?? 0,
            month: month ?? 0,
            closing_equity: period.closing_equity,
            contribution: period.contribution,
            dividends: period.dividends,
            perc_of_net_profit: period.perc_of_net_profit
          }
          if (mutateDataKey === `dividends`) {
            mutationPayload.dividends = value
          } else if (mutateDataKey === `perc_of_net_profit`) {
            mutationPayload.perc_of_net_profit = value
          } else if (mutateDataKey === `contribution`) {
            mutationPayload.contribution = value
          } else {
            return Promise.resolve()
          }
          return axiosInstance.post(`/financial-models/${financialModelId}/capital-structure/equity/data-entry`, mutationPayload)
        })
      )

      const queryPeriod = payload.queryPeriod ?? `forecast`
      const view = payload.view ?? `monthly`

      queryClient.invalidateQueries({
        queryKey: EQUITY_DATA_ENTRY_KEY.concat([financialModelId, queryPeriod, view])
      })
      queryClient.resetQueries({
        queryKey: EQUITY_TOTALS_KEY
      })
      invalidateGlobalsQueryKey([FINANCIAL_MODEL_SETTINGS_BASE_KEY])
    },
    [financialModelId, payload, queryClient]
  )

  const applyGrantValueToRow = useCallback(
    async (value: number, startFrom?: { year: number; month: number }, exactPeriod?: { year: number; month: number }) => {
      if (!financialModelId) return

      const mutateDataKey = payload?.mutateDataKey as GrantBaseFieldsKeys | undefined
      const periods = payload?.grantPeriods

      if (!mutateDataKey || !periods?.length) return

      const forecastRows = periods.filter(period => period.period_type === `forecast`)

      if (!forecastRows.length) return

      const rowsToUpdate = filterRowsStartingFrom(forecastRows, startFrom, exactPeriod)

      if (!rowsToUpdate.length) return

      await Promise.all(
        rowsToUpdate.map(period => {
          const { year, month } = parsePeriod(period.period)
          const { field_disabled: _ignored, ...configWithoutDisabled } = period.config
          const mutationPayload: UpdateGrantPeriod = {
            config: configWithoutDisabled as Exclude<GrantPeriodConfig, 'field_disabled'>,
            period_type: period.period_type,
            year: year ?? 0,
            month: month ?? 0,
            grant: value
          }
          return axiosInstance.post(`/financial-models/${financialModelId}/capital-structure/grant/data-entry`, mutationPayload)
        })
      )

      const queryPeriod = payload.queryPeriod ?? `forecast`
      const view = payload.view ?? `monthly`

      queryClient.invalidateQueries({
        queryKey: GRANT_DATA_ENTRY_KEY.concat([financialModelId, queryPeriod, view])
      })
      queryClient.resetQueries({
        queryKey: GRANT_TOTALS_KEY
      })
      invalidateGlobalsQueryKey([FINANCIAL_MODEL_SETTINGS_BASE_KEY])
    },
    [financialModelId, payload, queryClient]
  )

  const applyInventoryValueToRow = useCallback(
    async (value: number, startFrom?: { year: number; month: number }, exactPeriod?: { year: number; month: number }) => {
      if (!financialModelId) return
      const modelId = financialModelId

      const mutateDataKey = payload?.mutateDataKey as Exclude<InventoryPayloadKey, `config`> | undefined

      if (!mutateDataKey || !payload?.inventoryPeriods?.length) return

      const forecastRows = payload.inventoryPeriods.filter((period: InventoryPeriod) => period.period_type === `forecast`)

      if (!forecastRows.length) return

      const rowsToUpdate = filterRowsStartingFrom(forecastRows, startFrom, exactPeriod)

      if (!rowsToUpdate.length) return

      await Promise.all(
        rowsToUpdate.map(period => {
          const { year, month } = parsePeriod(period.period)

          const data = {
            ...period,
            [mutateDataKey]: value,
            year: year ?? 0,
            month: month ?? 0,
            period_type: period.period_type,
            product_id: payload.inventoryProductId ?? null,
            category_id: payload.inventoryCategoryId ?? null,
            config: {
              ...period.config,
              [mutateDataKey]: {
                type: period.config[mutateDataKey]?.type ?? `absolute`,
                allowed_types: period.config[mutateDataKey]?.allowed_types ?? []
              }
            }
          }

          return updateInventoryPeriodData({
            financialModelId: modelId,
            data
          })
        })
      )

      const queryPeriod = payload.queryPeriod ?? `forecast`
      const view = payload.view ?? `monthly`

      const inventoryPeriodQueryKey = createQueryKey({
        baseKey: TWC_BASE_KEY,
        keys: [`inventory`, `period-data`, modelId as string, queryPeriod as string, view as string]
      })

      queryClient.invalidateQueries({ queryKey: inventoryPeriodQueryKey as QueryKey })

      const inventoryTotalsQueryKey = createQueryKey({
        baseKey: TWC_TOTALS_KEY,
        keys: [`inventory`, modelId as string, queryPeriod as string, view as string]
      })

      queryClient.invalidateQueries({ queryKey: inventoryTotalsQueryKey as QueryKey })
    },
    [financialModelId, payload, queryClient]
  )

  const applyProvisionInventoryValueToRow = useCallback(
    async (value: number, startFrom?: { year: number; month: number }, exactPeriod?: { year: number; month: number }) => {
      if (!financialModelId) return

      const mutateDataKey = payload?.mutateDataKey as ProvisionInventoryBaseFieldsKeys | undefined
      const provisionPeriods = payload?.provisionInventoryPeriods
      const firstPeriod = provisionPeriods ? (provisionPeriods[0] as any) : undefined
      const derivedProductId =
        payload?.provisionInventoryProductId ?? firstPeriod?.product_id ?? firstPeriod?.productId ?? firstPeriod?.product?.id ?? null
      const categoryId = payload?.provisionInventoryCategoryId ?? firstPeriod?.category_id ?? null

      if (!mutateDataKey || !provisionPeriods?.length) return

      const forecastRows = provisionPeriods.filter(period => period.period_type === `forecast`)

      if (!forecastRows.length) return

      const rowsToUpdate = filterRowsStartingFrom(forecastRows, startFrom, exactPeriod)

      if (!rowsToUpdate.length) return

      await Promise.all(
        rowsToUpdate.map(period => {
          const { year, month } = parsePeriod(period.period)
          const { field_disabled: _ignored, ...configWithoutDisabled } = period.config
          const periodProductId = derivedProductId ?? (period as any).product_id ?? (period as any).productId ?? (period as any).product?.id ?? null

          const mutationPayload: ProvisionInventoryPeriodDataPostV2 = {
            config: configWithoutDisabled as Exclude<ProvisionInventoryPeriodConfig, 'field_disabled'>,
            period_type: period.period_type,
            year: year ?? 0,
            month: month ?? 0,
            product_id: periodProductId,
            category_id: categoryId,
            [mutateDataKey]: value
          }
          return axiosInstance.post(`/financial-models/${financialModelId}/provisions/data-entry/inventory`, mutationPayload)
        })
      )

      const queryPeriod = payload.queryPeriod ?? `forecast`
      const view = payload.view ?? `monthly`
      queryClient.invalidateQueries({
        queryKey: PROVISIONS_INVENTORY_KEY.concat([financialModelId, queryPeriod, view])
      })
      queryClient.resetQueries({
        queryKey: PROVISIONS_TOTALS_KEY
      })
    },
    [financialModelId, payload, queryClient]
  )

  const applyOtherProvisionsValueToRow = useCallback(
    async (value: number, startFrom?: { year: number; month: number }, exactPeriod?: { year: number; month: number }) => {
      if (!financialModelId) return

      const mutateDataKey = payload?.mutateDataKey as OtherProvisionsBaseFieldsKeys | undefined
      const provisionPeriods = payload?.otherProvisionsPeriods
      const productId = payload?.otherProvisionsProductId ?? null
      const categoryId = payload?.otherProvisionsCategoryId ?? null

      if (!mutateDataKey || !provisionPeriods?.length) return

      const forecastRows = provisionPeriods.filter(period => period.period_type === `forecast`)

      if (!forecastRows.length) return

      const rowsToUpdate = filterRowsStartingFrom(forecastRows, startFrom, exactPeriod)

      if (!rowsToUpdate.length) return

      await Promise.all(
        rowsToUpdate.map(period => {
          const { year, month } = parsePeriod(period.period)
          const { field_disabled: _ignored, ...configWithoutDisabled } = period.config
          const mutationPayload: OtherProvisionsPeriodDataPost = {
            config: configWithoutDisabled as Exclude<OtherProvisionsPeriodConfig, 'field_disabled'>,
            period_type: period.period_type,
            year: year ?? 0,
            month: month ?? 0,
            product_id: productId,
            category_id: categoryId,
            [mutateDataKey]: value
          }
          return axiosInstance.post(`/financial-models/${financialModelId}/provisions/data-entry/other`, mutationPayload)
        })
      )

      const queryPeriod = payload.queryPeriod ?? `forecast`
      const view = payload.view ?? `monthly`
      queryClient.invalidateQueries({
        queryKey: PROVISIONS_OTHER_KEY.concat([financialModelId, queryPeriod, view])
      })
      queryClient.resetQueries({
        queryKey: PROVISIONS_TOTALS_KEY
      })
    },
    [financialModelId, payload, queryClient]
  )

  const applyPayableValueToRow = useCallback(
    async (value: number, startFrom?: { year: number; month: number }, exactPeriod?: { year: number; month: number }) => {
      if (!financialModelId) return
      const modelId = financialModelId

      const mutateDataKey = payload?.mutateDataKey as PayableBaseFieldsKeys | undefined
      const setupId = payload?.payableSetupId

      if (!mutateDataKey || !setupId || !payload?.payablePeriods?.length) return

      const forecastRows = payload.payablePeriods.filter((period: PayablePeriod) => period.period_type === `forecast`)

      if (!forecastRows.length) return

      const rowsToUpdate = filterRowsStartingFrom(forecastRows, startFrom, exactPeriod)

      if (!rowsToUpdate.length) return

      await Promise.all(
        rowsToUpdate.map(period => {
          const { year, month } = parsePeriod(period.period)

          const data = {
            ...period,
            [mutateDataKey]: value,
            year: year ?? 0,
            month: month ?? 0,
            period_type: period.period_type,
            product_id: payload.payableProductId ?? null,
            cost_category_id: payload.payableCostCategoryId ?? null,
            config: {
              ...period.config,
              [mutateDataKey]: {
                type: period.config[mutateDataKey]?.type ?? `absolute`,
                allowed_types: period.config[mutateDataKey]?.allowed_types ?? []
              }
            }
          }

          return updatePayablePeriodData({
            financialModelId: modelId,
            setup_id: setupId,
            data
          })
        })
      )

      const queryPeriod = payload.queryPeriod ?? `forecast`
      const view = payload.view ?? `monthly`

      const payablePeriodQueryKey = createQueryKey({
        baseKey: TWC_BASE_KEY,
        keys: [`payable`, `period-data`, modelId as string, queryPeriod as string, view as string, setupId as string]
      })

      queryClient.invalidateQueries({ queryKey: payablePeriodQueryKey as QueryKey })

      const payableTotalsQueryKey = createQueryKey({
        baseKey: TWC_TOTALS_KEY,
        keys: [`payable`, modelId as string, queryPeriod as string, view as string]
      })

      queryClient.invalidateQueries({ queryKey: payableTotalsQueryKey as QueryKey })
    },
    [financialModelId, payload, queryClient]
  )

  const applyFixedAssetValueToRow = useCallback(
    async (value: number, startFrom?: { year: number; month: number }, exactPeriod?: { year: number; month: number }) => {
      if (!financialModelId) return

      const mutateDataKey = payload?.mutateDataKey as keyof AssetTypeEntryValues | undefined
      const assetTypeId = payload?.fixedAssetAssetTypeId

      if (!mutateDataKey || !assetTypeId || !payload?.fixedAssetPeriods?.length) return

      const forecastRows = payload.fixedAssetPeriods.filter((period: AssetTypeEntryPeriod) => period.period_type === `forecast`)

      if (!forecastRows.length) return

      const rowsToUpdate = filterRowsStartingFrom(forecastRows, startFrom, exactPeriod)

      if (!rowsToUpdate.length) return

      const { mutationFn } = createBulkUpdateFixedAssetEntriesMutationArgs()

      await Promise.all(
        rowsToUpdate.map(period => {
          const updatedConfig = {
            ...period.config,
            [mutateDataKey]: {
              type: period.config[mutateDataKey]?.type ?? `absolute`,
              allowed_types: period.config[mutateDataKey]?.allowed_types ?? []
            }
          }

          return mutationFn({
            ...period,
            config: updatedConfig,
            [mutateDataKey]: value,
            view: payload.view ?? `monthly`,
            financialModelId,
            assetTypeId
          })
        })
      )

      const queryPeriod = payload.queryPeriod ?? `forecast`
      const view = payload.view ?? `monthly`

      queryClient.invalidateQueries({
        queryKey: createAssetTypeEntriesQueryKey({
          financialModelId,
          assetTypeId,
          period: queryPeriod,
          view
        })
      })
    },
    [financialModelId, payload, queryClient]
  )

  const applyBadDebtValueToRow = useCallback(
    async (value: number, startFrom?: { year: number; month: number }, exactPeriod?: { year: number; month: number }) => {
      if (!financialModelId) return

      const mutateDataKey = payload?.mutateDataKey as BadDebtBaseFieldsKeys | undefined
      const provisionPeriods = payload?.provisionPeriods
      const firstPeriod = provisionPeriods ? (provisionPeriods[0] as BadDebtPeriodWithIds | undefined) : undefined
      const derivedProductId =
        payload?.provisionProductId ?? firstPeriod?.product_id ?? (firstPeriod as any)?.productId ?? (firstPeriod as any)?.product?.id ?? null
      const categoryId = payload?.provisionCategoryId ?? firstPeriod?.category_id ?? null

      if (!mutateDataKey || !provisionPeriods?.length) return

      const forecastRows = provisionPeriods.filter(period => period.period_type === `forecast`)

      if (!forecastRows.length) return

      const rowsToUpdate = filterRowsStartingFrom(forecastRows, startFrom, exactPeriod)

      if (!rowsToUpdate.length) return

      await Promise.all(
        rowsToUpdate.map(period => {
          const { year, month } = parsePeriod(period.period)
          const { field_disabled: _ignored, ...configWithoutDisabled } = period.config
          const periodProductId = derivedProductId ?? (period as any).product_id ?? (period as any).productId ?? (period as any).product?.id ?? null

          const mutationPayload: BadDebtPeriodDataPost = {
            config: configWithoutDisabled as Exclude<BadDebtPeriodConfig, 'field_disabled'>,
            period_type: period.period_type,
            year: year ?? 0,
            month: month ?? 0,
            product_id: periodProductId,
            category_id: categoryId,
            [mutateDataKey]: value
          }
          return axiosInstance.post(`/financial-models/${financialModelId}/provisions/data-entry/bad_debt`, mutationPayload)
        })
      )

      const queryPeriod = payload.queryPeriod ?? `forecast`
      const view = payload.view ?? `monthly`
      queryClient.invalidateQueries({
        queryKey: PROVISIONS_BAD_DEBT_KEY.concat([financialModelId, queryPeriod, view])
      })
      queryClient.resetQueries({
        queryKey: PROVISIONS_TOTALS_KEY
      })
    },
    [financialModelId, payload, queryClient]
  )

  const applyVatValueToRow = useCallback(
    async (value: number, startFrom?: { year: number; month: number }, exactPeriod?: { year: number; month: number }) => {
      if (!financialModelId) return

      const mutateDataKey = payload?.mutateDataKey as VatBaseFieldsKeys | undefined
      const vatModule = payload?.vatModule
      const vatType = payload?.vatType
      const vatPeriods = payload?.vatPeriods

      if (!mutateDataKey || !vatModule || !vatType || !vatPeriods?.length) return

      const forecastRows = vatPeriods.filter(period => period.period_type === `forecast`)

      if (!forecastRows.length) return

      const rowsToUpdate = filterRowsStartingFrom(forecastRows, startFrom, exactPeriod)

      if (!rowsToUpdate.length) return

      await Promise.all(
        rowsToUpdate.map(period => {
          const { year, month } = parsePeriod(period.period)
          const { field_disabled: _ignored, ...configWithoutDisabled } = period.config

          const vatBaseFields: VatPeriodBaseData = {
            aggregate: period.aggregate,
            vat_closing: period.vat_closing,
            vat_cur_period: period.vat_cur_period,
            vat_rate: period.vat_rate,
            perc_of_vat_eligible: period.perc_of_vat_eligible,
            vat_eligible: period.vat_eligible
          }

          const mutationPayload: VatPeriodDataPost = {
            config: configWithoutDisabled as VatPeriodDataPost['config'],
            period_type: period.period_type,
            year: year ?? 0,
            month: month ?? 0,
            ...vatBaseFields,
            [mutateDataKey]: value
          }

          return updateVatPeriodData(financialModelId, vatModule, vatType, mutationPayload)
        })
      )

      queryClient.invalidateQueries({
        queryKey: createQueryKey({
          baseKey: VAT_BASE_KEY,
          keys: [`period-data`, financialModelId, vatType]
        })
      })

      queryClient.invalidateQueries({
        queryKey: createQueryKey({
          baseKey: VAT_BASE_KEY,
          keys: [`totals-data`]
        })
      })
    },
    [financialModelId, payload, queryClient]
  )

  const applyValueToRow = useCallback(
    async (value: number, startFrom?: { year: number; month: number }, exactPeriod?: { year: number; month: number }) => {
      if (!financialModelId) return

      if (
        (payload?.marketingFunnel ||
          payload?.organicFunnel ||
          payload?.salesFunnel ||
          payload?.organicDirectResidualOrdersPeriods ||
          payload?.organicOrdersAllocationPeriods ||
          payload?.marketingOrderAllocationPeriods ||
          payload?.salesOrdersAllocationPeriods) &&
        payload?.mutateDataKey
      ) {
        await applyEcommerceChannelsValues({
          payload,
          value,
          startFrom,
          exactPeriod,
          financialModelId,
          queryClient
        })

        return
      }

      if (payload?.product && payload.mutateDataKey) {
        await applyPriceValues({
          payload,
          value,
          startFrom,
          exactPeriod,
          financialModelId,
          queryClient
        })
      }

      if (payload?.orderProduct && payload.mutateDataKey === `value`) {
        await applyOrderCompositionValueToRow(value, startFrom, exactPeriod, financialModelId, payload, queryClient)
        return
      }

      if (payload?.orderDataPeriods && payload.mutateDataKey && payload.mutateDataKey !== `value`) {
        await applyOrderDataValueToRow(value, startFrom, exactPeriod, financialModelId, payload, queryClient)
        return
      }

      if (payload?.customerSegmentRowKey && payload.customerSegmentPeriods) {
        await applySegmentValueToRow(value, startFrom, exactPeriod, financialModelId, payload, queryClient)
        return
      }

      if (payload?.directLabourPeriods && payload.mutateDataKey) {
        await applyDirectLabourValueToRow(value, startFrom, exactPeriod)
        return
      }

      if (payload?.directCostPeriods && payload.mutateDataKey) {
        await applyDirectCostValueToRow({
          value,
          startFrom,
          exactPeriod,
          financialModelId: financialModelId as string,
          payload,
          queryClient,
          ref: directCostRefetchTimer
        })

        return
      }

      if (payload?.personnelPeriods && payload?.departmentId && payload.mutateDataKey) {
        await applyPersonnelValueToRow(value, startFrom)
        return
      }

      if (payload?.opexPeriods && payload?.opexCategoryId && payload.mutateDataKey === `input_value`) {
        await applyOpexValueToRow(value, startFrom, exactPeriod)
        return
      }

      if (payload?.oicPeriods && payload?.oicCategoryId && payload.mutateDataKey === `input_value`) {
        await applyOicValueToRow(value, startFrom, exactPeriod)
        return
      }

      if (payload?.receivablePeriods && payload.mutateDataKey) {
        await applyReceivableValueToRow(value, startFrom, exactPeriod)
        return
      }

      if (payload?.owcPeriods && payload?.owcConfigType && payload.mutateDataKey) {
        await applyOwcValueToRow(value, startFrom, exactPeriod)
        return
      }

      if (payload?.grantPeriods && payload.mutateDataKey) {
        await applyGrantValueToRow(value, startFrom, exactPeriod)
        return
      }

      if (payload?.equityPeriods && payload.mutateDataKey) {
        await applyEquityValueToRow(value, startFrom, exactPeriod)
        return
      }

      if (payload?.provisionInventoryPeriods && payload.mutateDataKey) {
        await applyProvisionInventoryValueToRow(value, startFrom, exactPeriod)
        return
      }

      if (payload?.otherProvisionsPeriods && payload.mutateDataKey) {
        await applyOtherProvisionsValueToRow(value, startFrom, exactPeriod)
        return
      }

      if (payload?.inventoryPeriods && payload.mutateDataKey) {
        await applyInventoryValueToRow(value, startFrom, exactPeriod)
        return
      }

      if (payload?.payablePeriods && payload.mutateDataKey) {
        await applyPayableValueToRow(value, startFrom, exactPeriod)
        return
      }

      if (payload?.provisionPeriods && payload.mutateDataKey) {
        await applyBadDebtValueToRow(value, startFrom, exactPeriod)
        return
      }

      if (payload?.fixedAssetPeriods && payload.mutateDataKey && payload.fixedAssetAssetTypeId) {
        await applyFixedAssetValueToRow(value, startFrom, exactPeriod)
        return
      }

      if (payload?.vatPeriods && payload?.vatModule && payload?.vatType && payload.mutateDataKey) {
        await applyVatValueToRow(value, startFrom, exactPeriod)
      }
    },
    [
      financialModelId,
      payload,
      queryClient,
      applyDirectLabourValueToRow,
      applyPersonnelValueToRow,
      applyOpexValueToRow,
      applyOicValueToRow,
      applyReceivableValueToRow,
      applyOwcValueToRow,
      applyGrantValueToRow,
      applyEquityValueToRow,
      applyProvisionInventoryValueToRow,
      applyOtherProvisionsValueToRow,
      applyInventoryValueToRow,
      applyPayableValueToRow,
      applyBadDebtValueToRow,
      applyFixedAssetValueToRow,
      applyVatValueToRow
    ]
  )

  const applyRules = useCallback(async () => {
    const parsedValue = typeof numberValue === 'number' ? numberValue : Number(numberValue)
    const parsedRawValue = typeof rawValueInput === 'number' ? rawValueInput : Number(rawValueInput)
    const hasMutateKey = Boolean(payload?.mutateDataKey || payload?.customerSegmentRowKey)
    const startRange = startYear && startMonth ? { year: Number(startYear), month: Number(startMonth) } : undefined
    const hasNumberValue = numberValue !== `` && !Number.isNaN(parsedValue) && hasMutateKey
    const hasRawValue = Boolean(rawValueInput !== `` && !Number.isNaN(parsedRawValue) && startRange && hasMutateKey)

    const hasManualMonthlyValues =
      selectedFormulaOption === `manual_value_and_growth` &&
      manualBaseOption === 'monthly_base' &&
      Object.values(manualValuesByYear ?? {}).some(entry => entry && (entry.value ?? '') !== '' && (entry.percent ?? '') !== '')

    const hasManualYearlyValues =
      selectedFormulaOption === `manual_value_and_growth` &&
      manualBaseOption === 'yearly_base' &&
      Object.values(manualValuesByYear ?? {}).some(entry => entry && ((entry.value ?? '') !== '' || (entry.percent ?? '') !== ''))

    const hasManualGrowthValues = selectedFormulaOption === `manual_growth` && (manualGrowthPreviewData?.length ?? 0) > 0

    const canUseSmartFormula =
      selectedFormulaOption === `smart` && activeMethod === `formula` && smartMonthOptions.length > 0 && historicalMonthEntries.length > 0

    if (!hasRawValue && !hasNumberValue && !hasManualMonthlyValues && !hasManualYearlyValues && !canUseSmartFormula && !hasManualGrowthValues) {
      onClose()
      return
    }

    let applyTarget: () => Promise<unknown>

    if (activeMethod === `from`) {
      if (!hasRawValue) {
        onClose()

        return
      }

      applyTarget = () => applyValueToRow(parsedRawValue, startRange)
    } else if (activeMethod === `all`) {
      if (!hasNumberValue) {
        onClose()

        return
      }
      applyTarget = () => applyValueToRow(parsedValue)
    } else {
      applyTarget = hasRawValue && startRange ? () => applyValueToRow(parsedRawValue, startRange) : () => applyValueToRow(parsedValue)
    }

    const shouldBatchOrderCompositionFormula = activeMethod === `formula` && Boolean(payload?.orderProduct && payload?.mutateDataKey === `value`)
    const shouldBatchChannelsFormula =
      activeMethod === `formula` &&
      Boolean(
        (payload?.marketingFunnel ||
          payload?.organicFunnel ||
          payload?.salesFunnel ||
          payload?.organicDirectResidualOrdersPeriods ||
          payload?.organicOrdersAllocationPeriods ||
          payload?.marketingOrderAllocationPeriods ||
          payload?.salesOrdersAllocationPeriods) &&
          payload?.mutateDataKey
      )
    const shouldBatchPriceFormula = activeMethod === `formula` && Boolean(payload?.product && payload?.mutateDataKey)
    const shouldBatchSegmentFormula = activeMethod === `formula` && Boolean(payload?.customerSegmentRowKey && payload?.customerSegmentPeriods)
    const shouldBatchDirectCostFormula = activeMethod === `formula` && Boolean(payload?.directCostPeriods && payload?.mutateDataKey)
    const shouldBatchDirectLabourFormula = activeMethod === `formula` && Boolean(payload?.directLabourPeriods && payload?.mutateDataKey)
    const shouldBatchOrderDataFormula =
      activeMethod === `formula` && Boolean(payload?.orderDataPeriods && payload?.mutateDataKey && payload?.mutateDataKey !== `value`)
    const channelFormulaValuesByPeriod = new Map<string, number>()
    const priceFormulaValuesByPeriod = new Map<string, number>()
    const segmentFormulaValuesByPeriod = new Map<string, number>()
    const directCostFormulaValuesByPeriod = new Map<string, number>()
    const directLabourFormulaValuesByPeriod = new Map<string, number>()
    const orderCompositionFormulaValuesByPeriod = new Map<string, number>()
    const orderDataFormulaValuesByPeriod = new Map<string, number>()

    const queueFormulaApplication = (tasks: Promise<unknown>[], valueToApply: number, exactPeriod: { year: number; month: number }) => {
      if (shouldBatchChannelsFormula) {
        const periodKey = `${exactPeriod.year}-${String(exactPeriod.month).padStart(2, `0`)}`
        channelFormulaValuesByPeriod.set(periodKey, valueToApply)
        return
      }

      if (shouldBatchPriceFormula) {
        const periodKey = `${exactPeriod.year}-${String(exactPeriod.month).padStart(2, `0`)}`
        priceFormulaValuesByPeriod.set(periodKey, valueToApply)
        return
      }

      if (shouldBatchSegmentFormula) {
        const periodKey = `${exactPeriod.year}-${String(exactPeriod.month).padStart(2, `0`)}`
        segmentFormulaValuesByPeriod.set(periodKey, valueToApply)
        return
      }

      if (shouldBatchDirectCostFormula) {
        const periodKey = `${exactPeriod.year}-${String(exactPeriod.month).padStart(2, `0`)}`
        directCostFormulaValuesByPeriod.set(periodKey, valueToApply)
        return
      }

      if (shouldBatchDirectLabourFormula) {
        const periodKey = `${exactPeriod.year}-${String(exactPeriod.month).padStart(2, `0`)}`
        directLabourFormulaValuesByPeriod.set(periodKey, valueToApply)
        return
      }

      if (shouldBatchOrderCompositionFormula) {
        const periodKey = `${exactPeriod.year}-${String(exactPeriod.month).padStart(2, `0`)}`
        orderCompositionFormulaValuesByPeriod.set(periodKey, valueToApply)
        return
      }

      if (shouldBatchOrderDataFormula) {
        const periodKey = `${exactPeriod.year}-${String(exactPeriod.month).padStart(2, `0`)}`
        orderDataFormulaValuesByPeriod.set(periodKey, valueToApply)
        return
      }

      tasks.push(applyValueToRow(valueToApply, undefined, exactPeriod))
    }

    if (selectedFormulaOption === `smart` && activeMethod === `formula` && smartMonthOptions.length > 0) {
      const tasks: Promise<unknown>[] = []

      if (historicalMonthEntries.length) {
        const monthsToUse = Math.min(smartMonths, historicalMonthEntries.length)

        if (monthsToUse > 0) {
          const selectedMonths = historicalMonthEntries.slice(-monthsToUse)

          if (smartForecastMode === `linear`) {
            const firstValue = selectedMonths[0]?.value ?? 0
            const lastValue = selectedMonths[selectedMonths.length - 1]?.value ?? 0
            const growthRate = firstValue ? (lastValue - firstValue) / firstValue : 0
            const growthMultiplier = 1 + growthRate / monthsToUse

            if (includeSeasonal) {
              const baseYearKey = latestHistoricalYear ?? ``
              let previousYearValues: Record<string, number> = {}

              availableYears.forEach((year, yearIdx) => {
                const months = monthsByYear?.[String(year)] ?? []
                if (!months.length) return

                const nextYearValues: Record<string, number> = {}

                months.forEach(month => {
                  const normalized = month.value.padStart(2, `0`)
                  const referenceValue = yearIdx === 0 ? getBaseMonthValue(baseYearKey, normalized) : (previousYearValues[normalized] ?? 0)
                  const computedValue = Number((referenceValue * growthMultiplier).toFixed(2))
                  nextYearValues[normalized] = computedValue
                  const exactPeriod = { year: Number(year), month: Number(month.value) }
                  queueFormulaApplication(tasks, computedValue, exactPeriod)
                })

                previousYearValues = nextYearValues
              })
            } else {
              const latestYearKey = latestHistoricalYear ?? ``
              let previousYearTotal = historicalDistribution.totals[latestYearKey] ?? 0

              availableYears.forEach(year => {
                const months = monthsByYear?.[String(year)] ?? []
                if (!months.length) return

                previousYearTotal *= growthMultiplier
                const perMonth = Number((previousYearTotal / months.length).toFixed(2))

                months.forEach(month => {
                  const exactPeriod = { year: Number(year), month: Number(month.value) }
                  queueFormulaApplication(tasks, perMonth, exactPeriod)
                })
              })
            }
          } else {
            const average = selectedMonths.reduce((acc, entry) => acc + entry.value, 0) / selectedMonths.length

            if (includeSeasonal) {
              const latestYearKey = latestHistoricalYear ?? ``
              const yearHistoricTotal = historicalDistribution.totals[latestYearKey] ?? 0
              const monthHistoricValues = historicalDistribution.monthTotals[latestYearKey] ?? {}
              const annualized = average * 12

              availableYears.forEach(year => {
                const months = monthsByYear?.[String(year)] ?? []

                if (!months.length) return

                const fallbackShare = months.length ? 1 / months.length : 0

                months.forEach(month => {
                  const normalized = month.value.padStart(2, `0`)
                  const share = yearHistoricTotal > 0 ? (monthHistoricValues[normalized] ?? 0) / yearHistoricTotal : fallbackShare
                  const monthlyValue = Number((annualized * share).toFixed(2))
                  const exactPeriod = { year: Number(year), month: Number(month.value) }
                  queueFormulaApplication(tasks, monthlyValue, exactPeriod)
                })
              })
            } else {
              const normalizedAvg = Number(average.toFixed(2))
              availableYears.forEach(year => {
                const months = monthsByYear?.[String(year)] ?? []

                if (!months.length) return

                months.forEach(month => {
                  const exactPeriod = { year: Number(year), month: Number(month.value) }
                  queueFormulaApplication(tasks, normalizedAvg, exactPeriod)
                })
              })
            }
          }
        }
      }

      if (tasks.length) {
        applyTarget = () => Promise.all(tasks)
      }
    }

    if (selectedFormulaOption === `manual_growth` && activeMethod === `formula`) {
      const tasks: Promise<unknown>[] = []

      manualGrowthPreviewData.forEach(row => {
        row.rows.forEach(entry => {
          const exactPeriod = { year: Number(row.year), month: Number(entry.month.value) }
          queueFormulaApplication(tasks, entry.value, exactPeriod)
        })
      })

      if (tasks.length) {
        applyTarget = () => Promise.all(tasks)
      }
    }

    if (selectedFormulaOption === `manual_value_and_growth` && manualBaseOption === 'monthly_base' && activeMethod === `formula`) {
      const tasks: Promise<unknown>[] = []

      for (const year of availableYears) {
        const entry = manualValuesByYear[String(year)]

        if (!entry) continue

        const valStr = entry.value ?? ''
        const pctStr = entry.percent ?? ''

        if (valStr === '' || pctStr === '') continue

        const months = monthsByYear?.[String(year)] ?? []
        let current = Number(valStr)
        const rate = Number(pctStr) / 100

        for (const m of months) {
          const baseValue = Number(current.toFixed(2))
          const monthKey = `monthly-${year}-${m.value}`
          const overrideValue = parseNumericValue(monthlyValueOverrides[monthKey])
          const valueToApply = overrideValue ?? baseValue
          const exactPeriod = { year: Number(year), month: Number(m.value) }
          queueFormulaApplication(tasks, valueToApply, exactPeriod)
          current *= 1 + rate
        }
      }

      if (tasks.length) {
        applyTarget = () => Promise.all(tasks)
      }
    }

    if (selectedFormulaOption === `manual_value_and_growth` && manualBaseOption === 'yearly_base' && activeMethod === `formula`) {
      const tasks: Promise<unknown>[] = []
      let prevTotal: number | undefined
      let seasonalShares: number[] | undefined

      for (let idx = 0; idx < availableYears.length; idx++) {
        const year = availableYears[idx]
        const months = monthsByYear?.[String(year)] ?? []

        if (!months.length) continue

        if (idx === 0) {
          const entry = manualValuesByYear[String(year)]
          const valStr = entry?.value ?? ''

          if (valStr === '') continue

          const total = Number(valStr)

          if (Number.isNaN(total)) continue

          const applyHistoricalSeasonality =
            manualBaseOption === 'yearly_base' &&
            manualSeasonalityOption === 'include_seasonality' &&
            canUseHistoricalSeasonality &&
            latestHistoricalYear !== undefined &&
            forecastFirstYear !== undefined &&
            String(year) === forecastFirstYear

          const seasonalityYearKey = latestHistoricalYear ?? String(year)
          const fallbackShare = months.length ? 1 / months.length : 0

          const monthShares = months.map(m => {
            const shareFromHistorical = applyHistoricalSeasonality ? getHistoricalMonthShare(seasonalityYearKey, m.value) : undefined

            return shareFromHistorical !== undefined ? shareFromHistorical : fallbackShare
          })

          const monthValues = monthShares.map(share => Number((total * share).toFixed(2)))
          const distributedTotal = monthValues.reduce((acc, value) => acc + value, 0)
          const roundingAdjustment = Number((total - distributedTotal).toFixed(2))

          if (monthValues.length && Math.abs(roundingAdjustment) >= 0.0001) {
            monthValues[monthValues.length - 1] = Number((monthValues[monthValues.length - 1] + roundingAdjustment).toFixed(2))
          }

          if (manualSeasonalityOption === 'include_seasonality' && seasonalShares === undefined) {
            seasonalShares = monthShares
          }

          for (let monthIdx = 0; monthIdx < months.length; monthIdx++) {
            const m = months[monthIdx]
            const exactPeriod = { year: Number(year), month: Number(m.value) }
            const monthKey = `yearly-${year}-${m.value}`
            const overrideValue = parseNumericValue(yearlyValueOverrides[monthKey])
            const valueToApply = overrideValue ?? monthValues[monthIdx]
            queueFormulaApplication(tasks, valueToApply, exactPeriod)
          }

          prevTotal = total
        } else {
          const entry = manualValuesByYear[String(year)]
          const pctStr = entry?.percent ?? ''

          if (pctStr === '' || prevTotal === undefined) break

          const rate = Number(pctStr) / 100

          if (Number.isNaN(rate)) break

          const total = prevTotal * (1 + rate)
          const distributionShares = seasonalShares ?? (months.length ? months.map(() => 1 / months.length) : [])

          if (!distributionShares.length) break

          const monthValues = distributionShares.map(share => Number((total * share).toFixed(2)))
          const distributedTotalAfterShare = monthValues.reduce((acc, value) => acc + value, 0)
          const roundingAdjustmentAfterShare = Number((total - distributedTotalAfterShare).toFixed(2))

          if (monthValues.length && Math.abs(roundingAdjustmentAfterShare) >= 0.0001) {
            monthValues[monthValues.length - 1] = Number((monthValues[monthValues.length - 1] + roundingAdjustmentAfterShare).toFixed(2))
          }

          for (let monthIdx = 0; monthIdx < months.length; monthIdx++) {
            const m = months[monthIdx]
            const exactPeriod = { year: Number(year), month: Number(m.value) }
            const monthKey = `yearly-${year}-${m.value}`
            const overrideValue = parseNumericValue(yearlyValueOverrides[monthKey])
            const valueToApply = overrideValue ?? monthValues[monthIdx]
            queueFormulaApplication(tasks, valueToApply, exactPeriod)
          }

          prevTotal = total
        }
      }

      if (tasks.length) applyTarget = () => Promise.all(tasks)
    }

    if (shouldBatchChannelsFormula && channelFormulaValuesByPeriod.size > 0 && financialModelId && payload) {
      const periodValuesByKey = Object.fromEntries(channelFormulaValuesByPeriod)

      applyTarget = () =>
        applyEcommerceChannelsValues({
          payload: {
            ...payload,
            periodValuesByKey
          },
          value: parsedValue,
          financialModelId,
          queryClient
        })
    }

    if (shouldBatchPriceFormula && priceFormulaValuesByPeriod.size > 0 && financialModelId && payload) {
      const periodValuesByKey = Object.fromEntries(priceFormulaValuesByPeriod)

      applyTarget = () =>
        applyPriceValues({
          payload: {
            ...payload,
            periodValuesByKey
          },
          value: parsedValue,
          financialModelId,
          queryClient
        })
    }

    if (shouldBatchSegmentFormula && segmentFormulaValuesByPeriod.size > 0 && financialModelId && payload) {
      const periodValuesByKey = Object.fromEntries(segmentFormulaValuesByPeriod)

      applyTarget = () =>
        applySegmentValueToRow(
          parsedValue,
          undefined,
          undefined,
          financialModelId,
          {
            ...payload,
            periodValuesByKey
          },
          queryClient
        )
    }

    if (shouldBatchDirectCostFormula && directCostFormulaValuesByPeriod.size > 0 && financialModelId && payload) {
      const periodValuesByKey = Object.fromEntries(directCostFormulaValuesByPeriod)

      applyTarget = () =>
        applyDirectCostValueToRow({
          value: parsedValue,
          financialModelId,
          payload: {
            ...payload,
            periodValuesByKey
          },
          queryClient,
          ref: directCostRefetchTimer
        })
    }

    if (shouldBatchDirectLabourFormula && directLabourFormulaValuesByPeriod.size > 0 && financialModelId && payload) {
      const periodValuesByKey = Object.fromEntries(directLabourFormulaValuesByPeriod)

      applyTarget = () => applyDirectLabourValueToRow(parsedValue, undefined, undefined, periodValuesByKey)
    }

    if (
      shouldBatchOrderCompositionFormula &&
      orderCompositionFormulaValuesByPeriod.size > 0 &&
      financialModelId &&
      payload?.customerSegmentId &&
      payload?.orderProduct
    ) {
      const periodValuesByKey = Object.fromEntries(orderCompositionFormulaValuesByPeriod)
      const { customerSegmentId } = payload

      applyTarget = () =>
        applyOrderCompositionValues({
          financialModelId,
          customerSegmentId,
          orderProduct: payload.orderProduct,
          periodValuesByKey,
          queryClient
        })
    }

    if (
      shouldBatchOrderDataFormula &&
      orderDataFormulaValuesByPeriod.size > 0 &&
      financialModelId &&
      payload?.customerSegmentId &&
      payload?.orderDataPeriods &&
      payload?.mutateDataKey &&
      payload?.mutateDataKey !== `value`
    ) {
      const periodValuesByKey = Object.fromEntries(orderDataFormulaValuesByPeriod)
      const { customerSegmentId, orderDataPeriods, mutateDataKey } = payload

      applyTarget = () =>
        applyOrderDataValues({
          financialModelId,
          customerSegmentId,
          orderDataPeriods,
          mutateDataKey,
          periodValuesByKey,
          queryClient
        })
    }

    setApplyError(null)
    setIsApplying(true)

    try {
      await applyTarget()
      try {
        resetAllInputs()
      } catch (error) {
        console.error(error)
      }
      onClose()
    } catch (error) {
      setApplyError(getSidePanelApplyErrorMessage(error))
    } finally {
      setIsApplying(false)
    }
  }, [
    numberValue,
    rawValueInput,
    payload,
    startYear,
    startMonth,
    selectedFormulaOption,
    manualBaseOption,
    manualValuesByYear,
    manualGrowthPreviewData,
    activeMethod,
    smartMonthOptions.length,
    historicalMonthEntries,
    financialModelId,
    onClose,
    applyValueToRow,
    smartMonths,
    smartForecastMode,
    includeSeasonal,
    latestHistoricalYear,
    availableYears,
    monthsByYear,
    getBaseMonthValue,
    historicalDistribution.totals,
    historicalDistribution.monthTotals,
    monthlyValueOverrides,
    manualSeasonalityOption,
    canUseHistoricalSeasonality,
    forecastFirstYear,
    getHistoricalMonthShare,
    yearlyValueOverrides,
    queryClient,
    applyDirectLabourValueToRow,
    resetAllInputs
  ])

  useEffect(() => {
    let enterTimer: number | undefined
    let exitTimer: number | undefined

    if (open) {
      setMounted(true)
      enterTimer = window.setTimeout(() => setVisible(true), 20)
    } else {
      setVisible(false)
      exitTimer = window.setTimeout(() => setMounted(false), ANIMATION_DURATION)
    }

    return () => {
      if (enterTimer) window.clearTimeout(enterTimer)

      if (exitTimer) window.clearTimeout(exitTimer)
    }
  }, [open])

  useEffect(() => {
    if (!open) {
      resetAllInputs()
      setApplyError(null)
    }
  }, [open, resetAllInputs])

  useEffect(
    () => () => {
      if (directCostRefetchTimer.current) window.clearTimeout(directCostRefetchTimer.current)
      if (directLabourRefetchTimer.current) window.clearTimeout(directLabourRefetchTimer.current)
      if (opexRefetchTimer.current) window.clearTimeout(opexRefetchTimer.current)
      if (personnelRefetchTimer.current) window.clearTimeout(personnelRefetchTimer.current)
    },
    []
  )

  const asideRef = useRef<HTMLElement>(null)

  const handleBackdropPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!visible) return

      if (asideRef.current) {
        const rect = asideRef.current.getBoundingClientRect()
        const isInside = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom

        if (isInside) return
      }

      if (event.target === event.currentTarget) {
        onClose()
      }
    },
    [onClose, visible]
  )

  useEffect(() => {
    if (typeof document === `undefined`) return

    if (open) {
      document.body.dataset.sidePanelOpen = `true`
    } else if (document.body.dataset.sidePanelOpen === `true`) {
      delete document.body.dataset.sidePanelOpen
    }

    return () => {
      if (typeof document === `undefined`) return

      if (document.body.dataset.sidePanelOpen === `true`) {
        delete document.body.dataset.sidePanelOpen
      }
    }
  }, [open])

  useEffect(() => {
    if (typeof document === `undefined`) return undefined

    if (!open) return undefined

    const handlePointerDownCapture = (event: PointerEvent) => {
      const panelRoot = document.querySelector<HTMLElement>(`[data-sidepanel-root]`)

      if (!panelRoot) return

      const path = (event.composedPath?.() ?? []) as Node[]

      if (path.some(node => node && panelRoot.contains(node))) {
        event.stopPropagation()
      }
    }

    const handlePointerUpCapture = (event: PointerEvent) => {
      const panelRoot = document.querySelector<HTMLElement>(`[data-sidepanel-root]`)
      if (!panelRoot) return

      const path = (event.composedPath?.() ?? []) as Node[]
      if (path.some(node => node && panelRoot.contains(node))) {
        event.stopPropagation()
      }
    }

    document.addEventListener(`pointerdown`, handlePointerDownCapture, true)
    document.addEventListener(`pointerup`, handlePointerUpCapture, true)

    return () => {
      document.removeEventListener(`pointerdown`, handlePointerDownCapture, true)
      document.removeEventListener(`pointerup`, handlePointerUpCapture, true)
    }
  }, [open])

  if (!mounted) return null

  return (
    <>
      <div
        aria-hidden
        className={`fixed inset-0 z-40 bg-black/20 transition-opacity duration-300 ${
          visible ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onPointerDown={handleBackdropPointerDown}
      />
      <aside
        ref={asideRef}
        role='dialog'
        aria-modal='true'
        onPointerDown={e => {
          e.stopPropagation()
        }}
        className={`fixed right-4 top-4 z-50 h-[calc(100vh-2rem)] w-[520px] transform transition-all duration-300 ease-out ${
          visible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
        }`}
      >
        <div className='relative flex h-full flex-col gap-8 rounded-md bg-white shadow-xl'>
          <div className='absolute right-4 top-4'>
            <button type='button' aria-label='close side panel' onClick={onClose} className='text-black'>
              <X size={24} />
            </button>
          </div>
          <div className='px-8 pt-8 text-lg font-semibold'>Forecast Co-Pilot</div>
          <div className='flex flex-1 flex-col gap-8 overflow-y-auto px-8'>
            <div className=''>Select the rule you want to apply to insert data. You can always manually edit your values afterwards.</div>
            {applyError && (
              <div role='alert' aria-live='assertive' className='text-red-800 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm'>
                {applyError}
              </div>
            )}
            <div className=''>
              <div className='text-center text-lg font-semibold'>Choose your Rule</div>
            </div>

            <Accordion type='single' collapsible value={accordionValue} onValueChange={handleAccordionChange} className='flex flex-col gap-4'>
              <AccordionItem value='all' className='rounded-lg border px-4'>
                <AccordionTrigger className='hover:no-underline'>
                  <MethodAccordionTrigger title='Set & Keep' subtitle='Insert a constant value for all cells' />
                </AccordionTrigger>
                <AccordionContent>
                  <div className='flex flex-col gap-3 px-0.5 pt-2'>
                    <Input
                      type='text'
                      id='data-for-all-cells'
                      inputMode='numeric'
                      value={numberValue ?? ``}
                      onChange={e => setNumberValue(e.target.value)}
                      placeholder='Input Number'
                      variant='small'
                    />
                  </div>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value='from' className='rounded-lg border px-4'>
                <AccordionTrigger className='hover:no-underline'>
                  <MethodAccordionTrigger title='Flat from Month' subtitle='Insert a constant value from a month onward' />
                </AccordionTrigger>
                <AccordionContent>
                  <div className='flex items-center gap-3 px-0.5 pt-2'>
                    <Select
                      value={startYear ?? ``}
                      onValueChange={val => {
                        handleMethodActivation(`from`)
                        setStartYear(val)
                      }}
                    >
                      <SelectTrigger className='w-28'>
                        <SelectValue placeholder='Year' />
                      </SelectTrigger>
                      <SelectContent>
                        {availableYears.map(year => (
                          <SelectItem key={year} value={year} variant='small'>
                            {year}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={startMonth ?? ``}
                      onValueChange={val => {
                        handleMethodActivation(`from`)
                        setStartMonth(val)
                      }}
                    >
                      <SelectTrigger className='w-28'>
                        <SelectValue placeholder='Month' />
                      </SelectTrigger>
                      <SelectContent>
                        {availableMonthsForSelectedYear.map(month => (
                          <SelectItem key={month.value} value={month.value} variant='small'>
                            {month.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      className='!w-40'
                      type='text'
                      inputMode='numeric'
                      placeholder='Input Number'
                      value={rawValueInput as any}
                      onFocus={() => handleMethodActivation(`from`)}
                      onChange={e => setRawValueInput(e.target.value)}
                      variant='small'
                    />
                  </div>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value='formula' className='rounded-lg border px-4'>
                <AccordionTrigger className='hover:no-underline'>
                  <MethodAccordionTrigger title='Advanced Rules' subtitle='Choose among flexible methods to build a more sophisticated forecast' />
                </AccordionTrigger>
                <AccordionContent>
                  <div className='px-0.5 pt-2'>
                    <Select
                      value={selectedFormulaOption}
                      onValueChange={val => {
                        handleMethodActivation(`formula`)
                        setSelectedFormulaOption(val)
                      }}
                    >
                      <SelectTrigger className='w-full justify-between text-left'>
                        <SelectValue>
                          <span className='truncate'>{selectedFormulaLabel}</span>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className='max-w-[400px]'>
                        {availableFormulaOptions.map(option => (
                          <SelectItemWithIcon
                            key={option.value}
                            value={option.value}
                            icon={option.disabled ? <Lock className='opacity-50' /> : undefined}
                            iconPosition='right'
                            className={'w-full'}
                          >
                            <div className={`flex flex-col ${option.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                              <span className='font-medium'>{option.label}</span>
                              <span className='text-xs text-secondary'>{option.disabled ? '(Requires historical data)' : option.description}</span>
                            </div>
                          </SelectItemWithIcon>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedFormulaOption === `smart` && (
                      <div className='mt-4 flex flex-col gap-3'>
                        <div className='flex flex-col gap-1'>
                          <span className='text-sm font-medium'>Smart mode</span>
                          <RadioGroup
                            value={smartForecastMode}
                            onValueChange={val => {
                              handleMethodActivation(`formula`)
                              setSmartForecastMode(val as 'linear' | 'average')
                            }}
                            className='flex items-center gap-6'
                          >
                            <div className='flex items-center gap-2'>
                              <RadioGroupItem value='linear' className='flex-shrink-0' />
                              <span className='text-sm'>Linear regression</span>
                            </div>
                            <div className='flex items-center gap-2'>
                              <RadioGroupItem value='average' className='flex-shrink-0' />
                              <span className='text-sm'>Average</span>
                            </div>
                          </RadioGroup>
                        </div>
                        <div className='flex items-center gap-2'>
                          <span>Last</span>
                          <Select
                            value={smartMonths.toString()}
                            disabled={!smartMonthOptions.length}
                            onValueChange={val => {
                              handleMethodActivation(`formula`)
                              setSmartMonths(Number(val))
                            }}
                          >
                            <SelectTrigger className='h-8 w-20 justify-between text-left'>
                              <SelectValue>
                                <span className='truncate'>{smartMonths ?? `Months`}</span>
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent className='min-w-4'>
                              {smartMonthOptions.map(option => (
                                <SelectItem key={`smart-month-${option}`} value={option.toString()} variant='small'>
                                  {option}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <span>months</span>
                        </div>
                        <div className='flex items-center gap-3'>
                          <Checkbox
                            checked={includeSeasonal}
                            onCheckedChange={val => {
                              handleMethodActivation(`formula`)
                              setIncludeSeasonal(Boolean(val))
                            }}
                          />
                          <span>Include Seasonal Adjustments</span>
                        </div>
                        <p className='text-xs text-secondary'>
                          These options refer to the most recent months of historical data. Only durations that the history can support are shown.
                        </p>
                      </div>
                    )}
                    {selectedFormulaOption === `manual_growth` && (
                      <div className='mt-4 flex flex-col gap-2'>
                        <div className='flex flex-col gap-2'>
                          {availableYears.map(year => {
                            const baseOptionsForYear = getManualGrowthBaseYearOptionsForRow(year)
                            const hasBaseOptions = baseOptionsForYear.length > 0

                            return (
                              <div key={`manual-growth-row-${year}`} className='flex flex-wrap items-center gap-2 text-sm'>
                                {hasBaseOptions ? (
                                  <>
                                    <div className='whitespace-nowrap'>
                                      For <span className='font-medium'>{year}</span> apply
                                    </div>
                                    <Input
                                      type='text'
                                      inputMode='numeric'
                                      value={manualGrowthSettings[year]?.growth ?? '0'}
                                      variant='small'
                                      className='w-16'
                                      placeholder='0'
                                      onFocus={() => {
                                        handleMethodActivation(`formula`)
                                        setStartYear(year)
                                        const monthOptions = monthsByYear?.[year]
                                        if (monthOptions?.length) setStartMonth(monthOptions[0].value)

                                        setManualGrowthSettings(prev => {
                                          if (!prev[year]) {
                                            return {
                                              ...prev,
                                              [year]: {
                                                baseYear: baseOptionsForYear[0],
                                                growth: '0'
                                              }
                                            }
                                          }
                                          return prev
                                        })
                                      }}
                                      onChange={event => {
                                        const growthValue = event.target.value
                                        setManualGrowthSettings(prev => ({
                                          ...prev,
                                          [year]: {
                                            baseYear: prev[year]?.baseYear ?? baseOptionsForYear[0],
                                            growth: growthValue
                                          }
                                        }))
                                      }}
                                    />
                                    <div className='whitespace-nowrap'> % growth rate on</div>
                                    <div className='w-20'>
                                      <Select
                                        value={manualGrowthSettings[year]?.baseYear ?? ``}
                                        onValueChange={val => {
                                          handleMethodActivation(`formula`)
                                          setManualGrowthSettings(prev => ({
                                            ...prev,
                                            [year]: {
                                              baseYear: val,
                                              growth: prev[year]?.growth ?? ''
                                            }
                                          }))
                                        }}
                                      >
                                        <SelectTrigger className='w-full justify-between'>
                                          <SelectValue placeholder='Base Year' />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {baseOptionsForYear.map(option => (
                                            <SelectItem key={`manual-growth-base-${year}-${option}`} value={option} variant='small'>
                                              {option}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  </>
                                ) : (
                                  <div className='flex h-14 items-center text-xs text-secondary'>
                                    For first year {year} estimates please refer to another method
                                  </div>
                                )}
                              </div>
                            )
                          })}
                        </div>
                        {manualGrowthPreviewData.length > 0 && (
                          <div className='mt-3 overflow-x-auto'>
                            <div className='flex flex-nowrap gap-4'>
                              {manualGrowthPreviewData.map(entry => {
                                const { total } = entry

                                return (
                                  <PreviewTableWrapper
                                    key={`manual-growth-preview-${entry.year}`}
                                    title={`${entry.year} with base ${entry.baseYear}`}
                                    nTables={manualGrowthPreviewData.length}
                                  >
                                    {entry.rows.map(row => {
                                      const percentValue = total > 0 ? (row.value / total) * 100 : 0
                                      const percentInput = monthlyPercentDrafts[row.key] ?? percentValue.toFixed(2)

                                      return (
                                        <PreviewTableRow key={row.key} month={row.month.label}>
                                          <div className={PreviewTableInputValueWrapperClass}>
                                            <FormattedInput
                                              name={`manual-growth-value-${entry.year}-${row.month.value}`}
                                              type='text'
                                              inputMode='numeric'
                                              className={PreviewTableInputClass}
                                              value={monthlyValueOverrides[row.key] ?? row.value}
                                              onChange={val => handleMonthlyValueInputChange(row.key, val)}
                                            />
                                          </div>
                                          <div className={PreviewTableInputPercentageWrapperClass}>
                                            <input
                                              name={`manual-growth-percent-${entry.year}-${row.month.value}`}
                                              type='text'
                                              inputMode='decimal'
                                              className={PreviewTableInputClass}
                                              value={percentInput}
                                              onFocus={() => handleMethodActivation(`formula`)}
                                              onChange={event => handleMonthlyPercentDraftChange(row.key, event.target.value)}
                                              onBlur={event => handleMonthlyPercentCommit(row.key, event.target.value, row.value, total)}
                                            />
                                            <span className='text-sm text-secondary'>%</span>
                                          </div>
                                        </PreviewTableRow>
                                      )
                                    })}
                                    <PreviewTableBottomRow total={total.toFixed(2)} />
                                  </PreviewTableWrapper>
                                )
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    {selectedFormulaOption === `manual_value_and_growth` && (
                      <div className='mt-4 flex flex-col gap-2'>
                        <RadioGroup
                          value={manualBaseOption}
                          onValueChange={val => {
                            handleMethodActivation(`formula`)
                            setManualBaseOption(val as 'monthly_base' | 'yearly_base')
                            setManualValuesByYear({})
                            setManualSeasonalityOption('dont_include_seasonality')

                            if (val === 'monthly_base') {
                              if (availableYears?.length) setStartYear(availableYears[0])

                              if (availableMonthsForSelectedYear?.length) setStartMonth(availableMonthsForSelectedYear[0].value)
                            }
                          }}
                          className='flex items-center gap-6'
                        >
                          <div className='flex items-center gap-3'>
                            <RadioGroupItem value='monthly_base' className='flex-shrink-0' />
                            <div className='flex flex-col'>
                              <span className=''>Monthly base</span>
                            </div>
                          </div>
                          <div className='flex items-center gap-3'>
                            <RadioGroupItem value='yearly_base' className='flex-shrink-0' />
                            <div className='flex flex-col'>
                              <span className=''>Yearly base</span>
                            </div>
                          </div>
                        </RadioGroup>
                        {manualBaseOption === 'monthly_base' && (
                          <div className='mt-3 flex flex-col gap-2'>
                            <div className='flex flex-col gap-2'>
                              {availableYears.map(year => {
                                const monthsForYear = monthsByYear?.[String(year)] ?? []
                                const firstMonth = monthsForYear[0]
                                const monthValue = firstMonth?.value ?? ''

                                return (
                                  <div key={year} className='flex items-center gap-2 text-sm'>
                                    <div className='whitespace-nowrap'>
                                      For{' '}
                                      <span className=''>
                                        {firstMonth.label} {year}{' '}
                                      </span>{' '}
                                      apply
                                    </div>
                                    <Input
                                      name={`monthly-base-value-${year}`}
                                      type='text'
                                      inputMode='numeric'
                                      value={manualValuesByYear[String(year)]?.value ?? ''}
                                      variant='small'
                                      className='w-16 px-1'
                                      onFocus={() => {
                                        handleMethodActivation(`formula`)
                                        setStartYear(year)

                                        if (monthValue) setStartMonth(monthValue)

                                        setManualValuesByYear(prev => {
                                          if (!prev?.[String(year)]) {
                                            return {
                                              ...prev,
                                              [String(year)]: { value: '', percent: '0' }
                                            }
                                          }
                                          return prev
                                        })
                                      }}
                                      onChange={e => {
                                        const v = e.target.value
                                        setManualValuesByYear(prev => ({
                                          ...prev,
                                          [String(year)]: { value: v, percent: prev?.[String(year)]?.percent ?? '0' }
                                        }))
                                      }}
                                    />
                                    <div className='whitespace-nowrap text-sm'>and a growth rate of</div>
                                    <Input
                                      name={`monthly-base-percent-${year}`}
                                      type='text'
                                      inputMode='numeric'
                                      placeholder='0'
                                      value={manualValuesByYear[String(year)]?.percent ?? '0'}
                                      variant='small'
                                      className='w-10 text-right'
                                      onFocus={() => {
                                        handleMethodActivation(`formula`)
                                        setStartYear(year)
                                        if (monthValue) setStartMonth(monthValue)

                                        setManualValuesByYear(prev => {
                                          if (!prev?.[String(year)]) {
                                            return {
                                              ...prev,
                                              [String(year)]: { value: '', percent: '0' }
                                            }
                                          }
                                          return prev
                                        })
                                      }}
                                      onChange={e => {
                                        const p = e.target.value
                                        setManualValuesByYear(prev => ({
                                          ...prev,
                                          [String(year)]: { value: prev?.[String(year)]?.value ?? '0', percent: p }
                                        }))
                                      }}
                                    />{' '}
                                    %
                                  </div>
                                )
                              })}
                            </div>

                            <div className='mt-3 overflow-x-auto'>
                              <div className='flex flex-nowrap gap-4'>
                                {availableYears.map(year => {
                                  const hasInputs =
                                    (manualValuesByYear[String(year)]?.value ?? '') !== '' && (manualValuesByYear[String(year)]?.percent ?? '') !== ''

                                  if (!hasInputs) return null

                                  const months = monthsByYear?.[String(year)] ?? []
                                  const startRaw = Number(manualValuesByYear[String(year)]?.value ?? 0)
                                  const rate = Number(manualValuesByYear[String(year)]?.percent ?? 0) / 100

                                  const rawValues: number[] = []
                                  let current = startRaw

                                  for (let i = 0; i < months.length; i++) {
                                    rawValues.push(current)
                                    current *= 1 + rate
                                  }

                                  return (
                                    <PreviewTableWrapper key={year} title={String(year)} nTables={manualGrowthPreviewData.length}>
                                      {(() => {
                                        const rowEntries = months.map((month, idx) => {
                                          const monthKey = `monthly-${year}-${month.value}`
                                          const overrideValue = parseNumericValue(monthlyValueOverrides[monthKey])
                                          const baseValue = Number(rawValues[idx].toFixed(2))
                                          const value = overrideValue ?? baseValue

                                          return {
                                            month,
                                            key: monthKey,
                                            value
                                          }
                                        })

                                        const totalWithOverrides = rowEntries.reduce((acc, entry) => acc + entry.value, 0)

                                        return (
                                          <>
                                            {rowEntries.map(entry => {
                                              const percentValue = totalWithOverrides > 0 ? (entry.value / totalWithOverrides) * 100 : 0
                                              const percentInput = monthlyPercentDrafts[entry.key] ?? percentValue.toFixed(2)

                                              return (
                                                <PreviewTableRow key={entry.key} month={entry.month.label}>
                                                  <div className={PreviewTableInputValueWrapperClass}>
                                                    <FormattedInput
                                                      name={`monthly-base-value-${year}-${entry.month.value}`}
                                                      type='text'
                                                      inputMode='numeric'
                                                      className={PreviewTableInputClass}
                                                      value={monthlyValueOverrides[entry.key] ?? entry.value}
                                                      onChange={val => handleMonthlyValueInputChange(entry.key, val)}
                                                    />
                                                  </div>
                                                  <div className={PreviewTableInputPercentageWrapperClass}>
                                                    <input
                                                      name={`monthly-base-percent-${year}-${entry.month.value}`}
                                                      type='text'
                                                      inputMode='decimal'
                                                      className={PreviewTableInputClass}
                                                      value={percentInput}
                                                      onFocus={() => handleMethodActivation(`formula`)}
                                                      onChange={event => handleMonthlyPercentDraftChange(entry.key, event.target.value)}
                                                      onBlur={event =>
                                                        handleMonthlyPercentCommit(entry.key, event.target.value, entry.value, totalWithOverrides)
                                                      }
                                                    />
                                                    <span className=''>%</span>
                                                  </div>
                                                </PreviewTableRow>
                                              )
                                            })}
                                            <PreviewTableBottomRow total={totalWithOverrides.toFixed(2)} />
                                          </>
                                        )
                                      })()}
                                    </PreviewTableWrapper>
                                  )
                                })}
                              </div>
                            </div>
                          </div>
                        )}
                        {manualBaseOption === 'yearly_base' && (
                          <div className='mt-3 flex flex-col gap-2'>
                            <div className='flex flex-col gap-2'>
                              {availableYears.map((year, idx) => {
                                const monthsForYear = monthsByYear?.[String(year)] ?? []
                                const firstMonth = monthsForYear[0]
                                const monthValue = firstMonth?.value ?? ''

                                if (idx > 0) {
                                  const prevYear = availableYears[idx - 1]
                                  const prevEntry = manualValuesByYear[String(prevYear)]
                                  const prevFilled = prevEntry && ((prevEntry.value ?? '') !== '' || (prevEntry.percent ?? '') !== '')
                                  if (!prevFilled) return null
                                }

                                return (
                                  <div key={year} className='flex items-center gap-3 text-sm'>
                                    <div className='whitespace-nowrap'>
                                      {idx === 0 ? (
                                        <>
                                          For <span className=''>{year} </span> apply
                                        </>
                                      ) : (
                                        <>
                                          For <span className=''>{year} </span> apply a growth rate of
                                        </>
                                      )}
                                    </div>
                                    {idx === 0 ? (
                                      <Input
                                        name={`yearly-base-value-${year}`}
                                        className='max-w-44'
                                        type='text'
                                        inputMode='numeric'
                                        variant='small'
                                        value={manualValuesByYear[String(year)]?.value ?? ''}
                                        onFocus={() => {
                                          handleMethodActivation(`formula`)
                                          setStartYear(year)
                                          if (monthValue) setStartMonth(monthValue)

                                          setManualValuesByYear(prev => {
                                            if (!prev?.[String(year)]) {
                                              return {
                                                ...prev,
                                                [String(year)]: { value: '', percent: '0' }
                                              }
                                            }
                                            return prev
                                          })
                                        }}
                                        onChange={e => {
                                          const v = e.target.value
                                          setManualValuesByYear(prev => ({
                                            ...prev,
                                            [String(year)]: { value: v, percent: prev?.[String(year)]?.percent ?? '0' }
                                          }))
                                        }}
                                      />
                                    ) : (
                                      <>
                                        <Input
                                          name={`yearly-base-percent-${year}`}
                                          className='max-w-24'
                                          type='text'
                                          inputMode='numeric'
                                          placeholder='0'
                                          variant='small'
                                          value={manualValuesByYear[String(year)]?.percent ?? '0'}
                                          onFocus={() => {
                                            handleMethodActivation(`formula`)
                                            setStartYear(year)
                                            if (monthValue) setStartMonth(monthValue)

                                            setManualValuesByYear(prev => {
                                              if (!prev?.[String(year)]) {
                                                return {
                                                  ...prev,
                                                  [String(year)]: { value: '', percent: '0' }
                                                }
                                              }
                                              return prev
                                            })
                                          }}
                                          onChange={e => {
                                            const p = e.target.value
                                            setManualValuesByYear(prev => ({
                                              ...prev,
                                              [String(year)]: { value: prev?.[String(year)]?.value ?? '0', percent: p }
                                            }))
                                          }}
                                        />
                                        %
                                      </>
                                    )}
                                  </div>
                                )
                              })}
                            </div>
                            {canUseHistoricalSeasonality && (
                              <RadioGroup
                                value={manualSeasonalityOption}
                                onValueChange={val => {
                                  setManualSeasonalityOption(val as 'include_seasonality' | 'dont_include_seasonality')
                                }}
                                className='flex items-center'
                              >
                                <div className=''>Do you want to consider seasonality?</div>
                                <div className='flex items-center gap-3'>
                                  <RadioGroupItem value='include_seasonality' className='flex-shrink-0' />
                                  <div className='flex flex-col'>
                                    <span className=''>Yes</span>
                                  </div>
                                </div>
                                <div className='flex items-center gap-3'>
                                  <RadioGroupItem value='dont_include_seasonality' className='flex-shrink-0' />
                                  <div className='flex flex-col'>
                                    <span className=''>No</span>
                                  </div>
                                </div>
                              </RadioGroup>
                            )}
                            <div className='mt-3 overflow-x-auto'>
                              <div className='flex flex-nowrap gap-4'>
                                {(() => {
                                  const preview: Array<{
                                    year: string | number
                                    idx: number
                                    months: { value: string; label: string }[]
                                    total: number
                                    perMonth: number
                                  }> = []
                                  let prevTotal: number | undefined

                                  for (let idx = 0; idx < availableYears.length; idx++) {
                                    const year = availableYears[idx]
                                    const months = monthsByYear?.[String(year)] ?? []

                                    let hasInputs = false
                                    if (idx === 0) {
                                      hasInputs = (manualValuesByYear[String(year)]?.value ?? '') !== ''
                                    } else {
                                      const prevYear = availableYears[idx - 1]

                                      const prevFilled =
                                        (manualValuesByYear[String(prevYear)]?.value ?? '') !== '' ||
                                        (manualValuesByYear[String(prevYear)]?.percent ?? '') !== ''

                                      hasInputs =
                                        manualValuesByYear[String(year)]?.percent !== undefined &&
                                        (manualValuesByYear[String(year)]?.percent ?? '') !== '' &&
                                        prevFilled
                                    }

                                    if (!hasInputs) continue

                                    let total = 0

                                    if (idx === 0) {
                                      total = Number(manualValuesByYear[String(year)]?.value ?? 0)
                                    } else {
                                      const rate = Number(manualValuesByYear[String(year)]?.percent ?? 0) / 100
                                      total = prevTotal !== undefined ? prevTotal * (1 + rate) : 0
                                    }

                                    if (!months.length) continue

                                    const perMonth = months.length ? Number((total / months.length).toFixed(2)) : 0
                                    preview.push({ year, idx, months, total, perMonth })
                                    prevTotal = total
                                  }

                                  let storedSeasonalShares: number[] | undefined

                                  return preview.map((item, previewIdx) => {
                                    const applyHistoricalSeasonality =
                                      manualBaseOption === 'yearly_base' &&
                                      manualSeasonalityOption === 'include_seasonality' &&
                                      canUseHistoricalSeasonality &&
                                      latestHistoricalYear !== undefined &&
                                      previewIdx === 0

                                    const seasonalityYearKey = latestHistoricalYear ?? String(item.year)
                                    const yearHistoricTotal = historicalDistribution.totals[seasonalityYearKey] ?? 0
                                    const monthHistoricValues = historicalDistribution.monthTotals[seasonalityYearKey] ?? {}

                                    return (
                                      <PreviewTableWrapper
                                        key={`manual-growth-preview-${item.year}`}
                                        title={`${item.year}`}
                                        nTables={manualGrowthPreviewData.length}
                                      >
                                        {(() => {
                                          const rows = item.months.map((m, monthIndex) => {
                                            const fallbackShare = item.months.length ? 1 / item.months.length : 0

                                            const historicalShare =
                                              yearHistoricTotal > 0 ? (monthHistoricValues[m.value] ?? 0) / yearHistoricTotal : fallbackShare

                                            const isFirstYearWithSeasonality =
                                              manualBaseOption === 'yearly_base' &&
                                              manualSeasonalityOption === 'include_seasonality' &&
                                              previewIdx === 0

                                            let shareRatio: number

                                            if (isFirstYearWithSeasonality) {
                                              shareRatio = applyHistoricalSeasonality ? historicalShare : fallbackShare
                                              if (!storedSeasonalShares) {
                                                storedSeasonalShares = item.months.map(month => {
                                                  if (yearHistoricTotal > 0) {
                                                    return (monthHistoricValues[month.value] ?? 0) / yearHistoricTotal || fallbackShare
                                                  }
                                                  return fallbackShare
                                                })
                                              }
                                            } else if (manualSeasonalityOption === 'include_seasonality' && storedSeasonalShares) {
                                              shareRatio = storedSeasonalShares[monthIndex] ?? fallbackShare
                                            } else {
                                              shareRatio = fallbackShare
                                            }

                                            const baseValue = Number((item.total * shareRatio).toFixed(2))
                                            const monthKey = `yearly-${item.year}-${m.value}`
                                            const overrideValue = parseNumericValue(yearlyValueOverrides[monthKey])
                                            const value = overrideValue ?? baseValue

                                            return {
                                              key: monthKey,
                                              month: m,
                                              value
                                            }
                                          })

                                          const totalWithOverrides = rows.reduce((acc, entry) => acc + entry.value, 0)

                                          return (
                                            <>
                                              {rows.map((entry, index) => {
                                                const percentValue = totalWithOverrides > 0 ? (entry.value / totalWithOverrides) * 100 : 0
                                                const percentInput = yearlyPercentDrafts[entry.key] ?? percentValue.toFixed(2)

                                                return (
                                                  <PreviewTableRow key={entry.key} month={entry.month.label}>
                                                    <div className={PreviewTableInputValueWrapperClass}>
                                                      <FormattedInput
                                                        name={`yearly-base-value-${index}-${entry.month.value}`}
                                                        type='text'
                                                        inputMode='numeric'
                                                        className={PreviewTableInputClass}
                                                        value={yearlyValueOverrides[entry.key] ?? entry.value}
                                                        onChange={val => handleYearlyValueInputChange(entry.key, val)}
                                                      />
                                                    </div>
                                                    <div className={PreviewTableInputPercentageWrapperClass}>
                                                      <input
                                                        name={`yearly-base-percent-${index}-${entry.month.value}`}
                                                        type='text'
                                                        inputMode='decimal'
                                                        className={PreviewTableInputClass}
                                                        value={percentInput}
                                                        onFocus={() => handleMethodActivation(`formula`)}
                                                        onChange={event => handleYearlyPercentDraftChange(entry.key, event.target.value)}
                                                        onBlur={event =>
                                                          handleYearlyPercentCommit(entry.key, event.target.value, entry.value, totalWithOverrides)
                                                        }
                                                      />
                                                      <span className='text-sm text-secondary'>%</span>
                                                    </div>
                                                  </PreviewTableRow>
                                                )
                                              })}
                                              <PreviewTableBottomRow total={totalWithOverrides.toFixed(2)} />
                                            </>
                                          )
                                        })()}
                                      </PreviewTableWrapper>
                                    )
                                  })
                                })()}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
          <div className='bg-white px-8 pb-8'>
            <Button className='w-full' onClick={applyRules} disabled={isApplying}>
              Apply Rule
            </Button>
          </div>
        </div>
      </aside>
    </>
  )
}

export default SidePanel
